import React, { useEffect, useMemo, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Autocomplete from '@mui/material/Autocomplete';
import { apiUrl } from '../utils/api';
import { formatCurrency } from '../utils/numberFormatter';
import { TextField, Checkbox, Button, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, LinearProgress, Typography, Snackbar, Alert, Box, Chip, IconButton, Tooltip, Skeleton, CircularProgress } from '@mui/material';
import {
  GridColDef, GridRenderCellParams, GridActionsCellItem,
  GridRowModes, GridRowModesModel, GridRowEditStopReasons,
  GridRenderEditCellParams, GridEventListener, GridToolbar
} from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import StyledDataGrid from '../components/StyledDataGrid';
import { dataGridSx } from '../utils/dataGridStyles';

const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' };
const labelStyle = { fontSize: 12, color: '#555', display: 'block', margin: '4px 0' };

const Section = ({ title, children }: { title?: string; children: any }) => (
  <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: 10, padding: 12, marginBottom: 12 }}>
    {title && <h3 style={{ margin: '4px 0 10px', fontSize: 16 }}>{title}</h3>}
    {children}
  </div>
);

// Single-screen preview-style editor (replaces stepper)

const MaterialRequisitions = ({ projectId, access = 'none', userId, projects = [], deleteAllAccess }) => {
  const [existing, setExisting] = useState([]); // existing MRs for selected project
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false); // Dialog for Add/Edit MR
  const [poDialogOpen, setPoDialogOpen] = useState(false); // Dialog for viewing PO
  const [selectedPO, setSelectedPO] = useState(null); // PO data to display
  const [loadingPO, setLoadingPO] = useState(false); // Loading state for PO fetch
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Delete confirmation dialog
  const [mrToDelete, setMrToDelete] = useState(null); // MR serial to delete
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info' | 'warning'}>({
    open: false,
    message: '',
    severity: 'success'
  }); // Snackbar for success/error messages
  const [totalsBySerial, setTotalsBySerial] = useState({}); // { [serial]: number }
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [allSerials, setAllSerials] = useState([]); // global list of MR serials across projects
  const [viewMRDialog, setViewMRDialog] = useState<{open: boolean, data: any, items: any[], loadingItems: boolean}>({
    open: false, data: null, items: [], loadingItems: false
  });
  const [editingApprovedMR, setEditingApprovedMR] = useState(false); // true if editing an Approved MR (need to revert on save)
  const [isViewMode, setIsViewMode] = useState(false); // true when viewing MR (read-only)
  // Inline editor always enabled when user has write access

  const [header, setHeader] = useState({
    mr_serial_no: '',
    is_chargeable: false,
    status: 'Draft',
    mr_type: null, // NEW: 'Contract', 'VO', 'Design Change'
    date_of_issue: '',
    currency: '',
    gst: '',
    project_id: projectId,
    project_job_reference: '', // not stored on MR, used for preview only
    mr_description: '',
    document_location_id: null,
    mr_description2: '',
    vendor_name: '',
    remarks: '', // footer remarks
    note: '', // saved note under items (DB-backed)
    delivery_venue: '',
    delivery_recipient: '',
    delivery_hp: '',
    delivery_date: '',
    ordered_by: '',
    approved_by: '',
    contract_from: '',
    contract_to: '',
    defects_liability_months: '',
    warranty_months: '',
    maintenance_service: 'NA',
    created_by: '',
    design_change: '', // UI-only: selected design change number (change_number field)
    contract_id: null, // DB field: reference to contract
    vo_id: null, // NEW: DB field: reference to VO (variation order)
    budget_selection: null, // UI-only: selected budget item (design change or contract)
    rejection_comments: '',
    po_for: '',
  });
  const [designChangeOptions, setDesignChangeOptions] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);
  const [voOptions, setVoOptions] = useState([]); // NEW: VOs for MR Type dropdown
  const [mrForOptions, setMrForOptions] = useState([]); // NEW: Conditional options based on MR Type
  const [mrForLoading, setMrForLoading] = useState(false); // NEW: Loading state for MR For dropdown
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  const [items, setItems] = useState([
    { _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }
  ]);
  const [mrItemRowModesModel, setMrItemRowModesModel] = useState<GridRowModesModel>({});
  // Track the clicked MR-items row so the selection is visibly highlighted.
  const [selectedMrItemRowId, setSelectedMrItemRowId] = useState<any>(null);
  const [boqLineItems, setBoqLineItems] = useState([]);
  const [budgetCategories, setBudgetCategories] = useState([]);
  const [rateSheets, setRateSheets] = useState([]);
  const [selectedRateSheet, setSelectedRateSheet] = useState(null);
  const [rateSheetItems, setRateSheetItems] = useState([]);
  // Filters for left table
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fSerials, setFSerials] = useState([]);
  const [fTypes, setFTypes] = useState([]);
  const [fVendors, setFVendors] = useState([]);
  const [fDescs, setFDescs] = useState([]);
  const [fIssued, setFIssued] = useState([]);
  const [fReqBy, setFReqBy] = useState([]);
  const [fApprBy, setFApprBy] = useState([]);
  const fileInputRef = React.useRef(null);

  // Company data state
  const [companyData, setCompanyData] = useState({
    company_name: '',
    logo_url: '',
    address: '',
    number_format: '1,234.56'
  });
  const [companyDataLoading, setCompanyDataLoading] = useState(true);

  // Resolve user ID only where needed (import and item operations)

  // Helper to apply current filters; omit certain filters when computing option lists
  const applyRowFilters = useCallback((rows: any, omit: any = {}) => {
    const inSet = (arr: any, val: any) => !arr.length || arr.includes(val || '');
    return (rows || []).filter(row => (
      (omit.serials || inSet(fSerials, row.mr_serial_no)) &&
      (omit.types || inSet(fTypes, row.is_chargeable ? 'Chargeable' : 'Non-chargeable')) &&
      (omit.vendors || inSet(fVendors, row.vendor_name || '')) &&
      (omit.descs || inSet(fDescs, row.mr_description || '')) &&
      (omit.issued || inSet(fIssued, row.date_of_issue || '')) &&
      (omit.reqby || inSet(fReqBy, row.ordered_by || '')) &&
      (omit.apprby || inSet(fApprBy, row.approved_by || ''))
    ));
  }, [fSerials, fTypes, fVendors, fDescs, fIssued, fReqBy, fApprBy]);

  const filteredRows = useMemo(() => applyRowFilters(existing), [existing, applyRowFilters]);

  // NEW: Fetch MR For options based on MR Type
  const fetchMrForOptions = async (mrType: string, projId: number) => {
    try {
      setMrForLoading(true); // Start loading
      let endpoint = '';
      if (mrType === 'Contract') {
        endpoint = `/api/material-requisitions/options/contracts/${projId}`;
      } else if (mrType === 'VO') {
        endpoint = `/api/material-requisitions/options/vos/${projId}`;
      } else if (mrType === 'Design Change') {
        endpoint = `/api/material-requisitions/options/design-changes/${projId}`;
      }

      if (!endpoint) {
        setMrForOptions([]);
        setMrForLoading(false);
        return;
      }

      const res = await fetch(apiUrl(endpoint), {
        credentials: 'include'
      });
      if (!res.ok) {
        console.error('Failed to fetch MR For options');
        setMrForOptions([]);
        setMrForLoading(false);
        return;
      }

      const data = await res.json();

      // Transform data to common format {id, label}
      let options = [];
      if (mrType === 'Contract' && data.contracts) {
        options = data.contracts.map((c: any) => ({
          id: c.contract_id,
          label: `${c.contract_number} - ${c.title || 'Untitled'}`
        }));
      } else if (mrType === 'VO' && data.vos) {
        options = data.vos.map((vo: any) => ({
          id: vo.vo_id,
          label: `${vo.contract_number} - ${vo.title || 'Untitled'}`
        }));
      } else if (mrType === 'Design Change' && data.design_changes) {
        options = data.design_changes.map((dc: any) => ({
          id: dc.change_number,
          label: `${dc.change_number} - ${dc.change_name || 'Untitled'}`
        }));
      }

      setMrForOptions(options);
      setMrForLoading(false); // Stop loading
    } catch (error) {
      console.error('Error fetching MR For options:', error);
      setMrForOptions([]);
      setMrForLoading(false); // Stop loading on error
    }
  };

  // Import dialogs (similar to Documents page)
  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState(null);
  const [fileHeaders, setFileHeaders] = useState([]);
  // DB-aligned mapping fields (backward-compatible parsing handled in import logic)
  const MR_FIELDS = [
    // Header fields
    'mr_serial_no',
    'is_chargeable',
    'vendor_name',
    'mr_description',
    'date_of_issue',
    'currency',
    'created_by',
    'gst',
    'ordered_by',
    'approved_by',
    'delivery_date',
    'delivery_venue',
    'delivery_recipient',
    'delivery_hp',
    'remarks',
    'note',
    'document_location',
    'change_number',
    'contract_from',
    'contract_to',
    'defects_liability_months',
    'warranty_months',
    'maintenance_service',
    // Item fields
    'item_no',
    'item_description',
    'item_quantity',
    'item_measurement',
    'buying_price',
    'charging_price',
    'delivery_charge',
    'category_id',
  ];
  const [mapping, setMapping] = useState(MR_FIELDS.reduce((a,f)=>({ ...a, [f]: ''}), {}));
  // Import progress (like Documents page)
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  useEffect(() => {
    if (!importing) return;
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p));
    }, 500);
    return () => clearInterval(timer);
  }, [importing]);

  const handleImportClick = () => setImportSourceOpen(true);
  const handleImportSource = (isThird) => {
    setImportSourceOpen(false);
    if (isThird) {
      setMappingOpen(true);
    } else {
      // template
      fileInputRef.current?.click();
    }
  };
  const handleMapFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMapFile(f);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows: any = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
      const headers = ((rows as any)[0] || []).map((v: any) => String(v || ''));
      setFileHeaders(headers);
      // naive auto-map by matching names (case/underscore-insensitive)
      const auto = { ...mapping };
      MR_FIELDS.forEach(fld => {
        const idx = headers.findIndex(h => h.toLowerCase().replace(/\s+/g,'_') === fld.toLowerCase());
        if (idx >= 0) auto[fld] = headers[idx];
      });
      setMapping(auto);
    } catch {
      setFileHeaders([]);
    }
  };
  const handleMappingChange = (field) => (e) => setMapping(prev => ({ ...prev, [field]: e.target.value }));

  // Normalized import that both Template and Mapping use
  const importRows = async (rows) => {
    const normalizeDate = (val) => {
      try {
        if (val == null || val === '') return '';
        // Date object
        if (Object.prototype.toString.call(val) === '[object Date]') {
          if (isNaN(val.getTime())) return '';
          return new Date(Date.UTC(val.getFullYear(), val.getMonth(), val.getDate())).toISOString().slice(0, 10);
        }
        // Excel serial number
        if (typeof val === 'number') {
          const ssf = XLSX?.SSF;
          if (ssf && typeof ssf.parse_date_code === 'function') {
            const d = ssf.parse_date_code(val);
            if (d && d.y && d.m && d.d) {
              return new Date(Date.UTC(d.y, d.m - 1, d.d)).toISOString().slice(0, 10);
            }
          }
          // Fallback: treat as days since 1899-12-30 (Excel epoch)
          const epoch = new Date(Date.UTC(1899, 11, 30));
          const ms = Math.round(val * 86400000);
          const dt = new Date(epoch.getTime() + ms);
          return dt.toISOString().slice(0, 10);
        }
        // String formats
        const s = String(val).trim();
        if (!s) return '';
        // If already ISO-like, strip time
        const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
        // MM/DD/YYYY or M/D/YYYY
        let m = s.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
        if (m) {
          const mm = String(m[1]).padStart(2, '0');
          const dd = String(m[2]).padStart(2, '0');
          return `${m[3]}-${mm}-${dd}`;
        }
        // DD/MM/YYYY or D/M/YYYY
        m = s.match(/^([0-9]{1,2})-([0-9]{1,2})-([0-9]{4})$/) || s.match(/^([0-9]{1,2})\.([0-9]{1,2})\.([0-9]{4})$/) || s.match(/^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/);
        if (m) {
          // Heuristic: if first part > 12, treat as DD/MM/YYYY
          const a = parseInt(m[1], 10);
          const b = parseInt(m[2], 10);
          if (a > 12 && b <= 12) {
            const dd = String(a).padStart(2, '0');
            const mm = String(b).padStart(2, '0');
            return `${m[3]}-${mm}-${dd}`;
          }
        }
        // Last resort: Date.parse
        const dtry = new Date(s);
        if (!isNaN(dtry.getTime())) {
          return new Date(Date.UTC(dtry.getFullYear(), dtry.getMonth(), dtry.getDate())).toISOString().slice(0, 10);
        }
      } catch {}
      return '';
    };
    setImporting(true);
    setImportProgress(0);
    const sess = (() => { try { return JSON.parse(sessionStorage.getItem('session') || localStorage.getItem('session') || '{}'); } catch { return {}; } })();
    const userIdResolved = userId || (sess && sess.user && sess.user.user_id);
    const overwrite = window.confirm('Overwrite existing MRs with same serial within THIS project? Click OK to overwrite, Cancel to import only new.');
    let _added = 0, _overwritten = 0, _skipped = 0, _failed = 0;
    const _logRows: { label: string; status: string }[] = [];
    // Load locations for name->id mapping
    let locByName = new Map();
      try {
        const lr = await fetch(apiUrl(`/api/document-locations?project_id=${projectId}`));
        const ls = lr.ok ? await lr.json() : [];
        (ls || []).forEach(l => {
          const n = String(l.document_location || l.name || '').trim().toLowerCase();
          const id = l.document_location_id ?? l.id;
          if (n && id != null) locByName.set(n, id);
        });
      } catch {}
    // build maps: serial -> items, and serial -> first header row
    const bySerial = {};
    const headerBySerial = new Map();
    const pushItem = (serial, it) => {
      if (!serial) return;
      (bySerial[serial] ||= []).push(it);
    };
    // Pre-scan to index items and capture a canonical header row per serial
    for (const r of rows) {
      const serial = String(r.SerialNo || r.mr_serial_no || '').trim();
      if (!serial) continue;
      // If this row contains only item fields (no header fields), skip header upsert for this row
      const hasAnyHeaderField = (
        (r.MrType ?? r.type ?? r.is_chargeable ?? '') !== '' ||
        (r.ItemNote ?? r.mr_description ?? '') !== '' ||
        (r.Vendor ?? r.vendor_name ?? '') !== '' ||
        (r.DateOfIssue ?? r.date_of_issue ?? '') !== '' ||
        (r.Currency ?? r.currency ?? '') !== '' ||
        (r.GST ?? r.gst ?? '') !== '' ||
        (r.OrderedBy ?? r.ordered_by ?? '') !== '' ||
        (r.ApprovedBy ?? r.approved_by ?? '') !== '' ||
        (r.DeliveryDate ?? r.delivery_date ?? '') !== '' ||
        (r.DeliveryVenue ?? r.delivery_venue ?? '') !== '' ||
        (r.DeliveryRecipient ?? r.delivery_recipient ?? '') !== '' ||
        (r.DeliveryHp ?? r.delivery_hp ?? '') !== '' ||
        (r.Contract_from ?? r.contract_from ?? '') !== '' ||
        (r.Contract_to ?? r.contract_to ?? '') !== '' ||
        (r['DLP_Warranty'] ?? r.defects_liability_months ?? r.warranty_months ?? '') !== '' ||
        (r.Maintenance_Services ?? r.maintenance_service ?? '') !== ''
      );
      if (!hasAnyHeaderField) {
        // no header data -> defer to the row that has header fields
        continue;
      }
      if (!headerBySerial.has(serial)) headerBySerial.set(serial, r);
      const item = {
        item_no: r.ItemNo ?? r.item_no,
        item_description: r.ItemDscr ?? r.item_description,
        item_quantity: r.ItemQty ?? r.item_quantity,
        item_measurement: r.ItemMeasure ?? r.item_measurement,
        buying_price: r.ItemBuying ?? r.buying_price,
        charging_price: r.ItemCharging ?? r.charging_price,
        delivery_charge: r.ItemDelivery ?? r.delivery_charge,
        category_id: r.category_id,
        bli_id: r.bli_id || null,
      };
      if (item.item_no || item.item_description || item.item_quantity || item.item_measurement || item.buying_price || item.charging_price || item.delivery_charge) {
        pushItem(serial, item);
      }
    }
    // Only overwrite within current project; warn if serial belongs elsewhere
    const existingSerials = new Set((existing || []).map(x => x.mr_serial_no));
    const allSerialsSet = new Set(allSerials || []);
    // Process each serial once using its canonical header row
    for (const [serial, r] of headerBySerial.entries()) {
      const existsHere = existingSerials.has(serial);
      const existsElsewhere = allSerialsSet.has(serial) && !existsHere;
      if (existsElsewhere) {
        _skipped++; _logRows.push({ label: serial, status: 'Skipped (exists in another project)' });
        continue;
      }
      if (!overwrite && existsHere) { _skipped++; _logRows.push({ label: serial, status: 'Skipped (already exists)' }); continue; }
      // Check duplicates for this serial once before any requests
      const itsRaw = (bySerial[serial] || []).filter(Boolean).map((it, i) => ({
        item_no: it.item_no || (i + 1),
        item_description: it.item_description || '',
        item_quantity: it.item_quantity === '' ? '' : Number(it.item_quantity || 0),
        item_measurement: it.item_measurement || 'lot',
        buying_price: it.buying_price === '' ? '' : Number(it.buying_price || 0),
        // currency added after header body defined; not needed for duplicate check
        charging_price: undefined,
        delivery_charge: it.delivery_charge === '' ? '' : Number(it.delivery_charge || 0),
      }));
      const numCounts = new Map();
      for (const rIt of itsRaw) {
        const n = Number(rIt.item_no);
        if (Number.isInteger(n) && n > 0) numCounts.set(n, (numCounts.get(n) || 0) + 1);
      }
      const dupNos = Array.from(numCounts.entries()).filter(([, c]) => c > 1).map(([n]) => n);
      if (dupNos.length) {
        _failed++; _logRows.push({ label: serial, status: `Failed (duplicate item numbers: ${dupNos.join(', ')})` });
        continue;
      }

      const monthsVal = r['DLP_Warranty'] ?? r.defects_liability_months ?? r.warranty_months;
      const months = (monthsVal === '' || monthsVal === undefined || monthsVal === null) ? null : Number(monthsVal);
      const typeRaw = (r.MrType ?? r.type ?? r.is_chargeable ?? '').toString().trim().toLowerCase();
      const chargeable = typeof r.is_chargeable === 'boolean'
        ? r.is_chargeable
        : (typeRaw === 'chargeable' || typeRaw === 'yes' || typeRaw === 'true' || typeRaw === '1');
      // normalize maintenance_service to DB-allowed values
      const normalizeMaint = (v) => {
        const s = String(v || '').trim().toLowerCase();
        if (!s) return 'NA';
        if (['monthly'].includes(s)) return 'Monthly';
        if (['bi-monthly','bi monthly','bimonthly','bi monthly.','bi-monthly.'].includes(s)) return 'Bi-Monthly';
        if (['quarterly'].includes(s)) return 'Quarterly';
        if (['yearly','annually','annual'].includes(s)) return 'Yearly';
        if (['na','n.a.','n/a','none'].includes(s)) return 'NA';
        return 'NA';
      };
      const body = {
        mr_serial_no: serial,
        is_chargeable: chargeable,
        date_of_issue: normalizeDate(r.DateOfIssue ?? r.date_of_issue ?? r.issue_date ?? ''),
        currency: r.Currency ?? r.currency ?? '',
        gst: r.GST ?? r.gst ?? '',
        project_id: projectId,
        mr_description: r.ItemNote ?? r.mr_description ?? '',
        document_location_id: (r.document_location_id ?? r.Document_Location_ID ?? null) || (locByName.get(String(r.document_location || r.Location || '').trim().toLowerCase()) || null),
        vendor_name: r.Vendor ?? r.vendor_name ?? '',
        remarks: r.Remarks ?? r.remarks ?? '',
        delivery_venue: r.DeliveryVenue ?? r.delivery_venue ?? '',
        delivery_recipient: r.DeliveryRecipient ?? r.delivery_recipient ?? '',
        delivery_hp: r.DeliveryHp ?? r.delivery_hp ?? '',
        delivery_date: normalizeDate(r.DeliveryDate ?? r.delivery_date ?? ''),
        ordered_by: r.OrderedBy ?? r.ordered_by ?? '',
        approved_by: r.ApprovedBy ?? r.approved_by ?? '',
        contract_from: normalizeDate(r.Contract_from ?? r.contract_from ?? ''),
        contract_to: normalizeDate(r.Contract_to ?? r.contract_to ?? ''),
        defects_liability_months: months,
        warranty_months: months,
        maintenance_service: normalizeMaint(r.Maintenance_Services ?? r.maintenance_service ?? 'NA'),
        note: (r.note ?? r.Note ?? ''),
        change_number: (() => { const v = (r.change_number ?? r.DesignChange ?? r.design_change ?? ''); const s = String(v || '').trim(); return s || null; })(),
      };
      // Only overwrite within this project
      let method = overwrite && existsHere ? 'PUT' : 'POST';
      const url = method === 'PUT'
        ? apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}`)
        : apiUrl('/api/material-requisitions');
      // For PUT, avoid moving MRs between projects by omitting project_id
      const bodyToSend = (method === 'PUT') ? (({ project_id, ...rest }) => rest)(body) : body;
      let rh = await fetch(url, { method, headers: { 'Content-Type': 'application/json', ...(userIdResolved ? { 'X-User-ID': userIdResolved } : {}) }, body: JSON.stringify(bodyToSend) });
      if (!rh.ok) {
        // Fallback: if POST failed due to stale existence check, verify which project owns it
        if (method === 'POST' && overwrite) {
          const chk = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}`));
          if (chk.ok) {
            const existingHeader = await chk.json();
            if (existingHeader && existingHeader.project_id === projectId) {
              // It exists here: switch to update (PUT) without project_id
              method = 'PUT';
              const putUrl = apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}`);
              rh = await fetch(putUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(userIdResolved ? { 'X-User-ID': userIdResolved } : {}) }, body: JSON.stringify((({ project_id, ...rest }) => rest)(body)) });
            } else {
              let msg = 'Failed to save MR header';
              try { const data = await rh.json(); if (data && data.error) msg = data.error; } catch {}
              _failed++; _logRows.push({ label: serial, status: `Failed: ${msg}. Serial exists in another project.` });
              continue;
            }
          }
        }
        if (!rh.ok) {
          let msg = 'Failed to save MR header';
          try { const data = await rh.json(); if (data && data.error) msg = data.error; } catch {}
          if (method === 'POST' && !overwrite) {
            msg = `${msg}. Serial already exists in this project. Use Overwrite to update.`;
          }
          _failed++; _logRows.push({ label: serial, status: `Failed: ${msg}` });
          continue;
        }
      }
      const its = (bySerial[serial] || []).filter(Boolean).map((it, i) => ({
        item_no: it.item_no || (i + 1),
        item_description: it.item_description || '',
        item_quantity: it.item_quantity === '' ? '' : Number(it.item_quantity || 0),
        item_measurement: it.item_measurement || 'lot',
        buying_price: it.buying_price === '' ? '' : Number(it.buying_price || 0),
        currency: body.currency || undefined,
        charging_price: body.is_chargeable ? (it.charging_price === '' ? '' : Number(it.charging_price || 0)) : null,
        delivery_charge: it.delivery_charge === '' ? '' : Number(it.delivery_charge || 0),
      }));
      const bulkResp = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}/items/bulk`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(userIdResolved ? { 'X-User-ID': userIdResolved } : {}) },
        body: JSON.stringify({ items: its })
      });
      if (!bulkResp.ok) {
        let msg = 'Failed to save items';
        try { const data = await bulkResp.json(); if (data && data.error) { msg = data.error; if (data.duplicates && data.duplicates.length) msg += ` Duplicates: ${data.duplicates.join(', ')}`; } } catch {}
        _failed++; _logRows.push({ label: serial, status: `Failed: ${msg}` });
        continue;
      }
      if (method === 'PUT') { _overwritten++; _logRows.push({ label: serial, status: 'Overwritten' }); }
      else { _added++; _logRows.push({ label: serial, status: 'Added' }); }
    }
    try {
      fetchExisting();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      setImportSummaryData({ total: headerBySerial.size, added: _added, overwritten: _overwritten, skipped: _skipped, failed: _failed, rows: _logRows });
      setImportSummaryOpen(true);
      setImportProgress(100);
    } finally {
      setImporting(false);
    }
    return;
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Added') { status = 'Pass'; notes = 'New Item added' }
      else if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s.includes('Skipped')) { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else { status = 'Failed'; notes = s.replace(/^Failed:\s*/i, '') }
      return { row_no: i + 2, mr_serial: r.label, status, notes }
    })
    fetch(apiUrl('/api/material-requisitions/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'MR_Import_Log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleMappingSubmit = async () => {
    if (!mapFile) { alert('Select a file'); return; }
    try {
      const buf = await mapFile.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      // Normalize using mapping
      const norm = rows.map(r => MR_FIELDS.reduce((obj, fld) => ({ ...obj, [fld]: r[mapping[fld]] ?? '' }), {}));
      setMappingOpen(false);
      await importRows(norm);
    } catch (err) {
      alert(err.message || 'Mapping import failed');
    }
  };

  // Export Template (blue headers via backend)
  const exportTemplate = () => {
    fetch(apiUrl('/api/material-requisitions/template'))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'MR_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to download template'));
  };

  // Export current table (summary or detailed)
  const exportFiltered = async () => {
    const detailed = window.confirm('Export DETAILED table (OK) or SUMMARY table (Cancel)?');
    try {
    if (!detailed) {
      // Summary: export current visible table columns
      const data = [
        ['MR Serial No.','Type','Vendor','Description','Issued','Requested By','Approved By','Grand Total (no GST)'],
        ...filteredRows.map(r => [
          r.mr_serial_no,
          r.is_chargeable ? 'Chargeable' : 'Non-chargeable',
          r.vendor_name || '',
          r.mr_description || '',
          r.date_of_issue || '',
          r.ordered_by || '',
          r.approved_by || '',
          (totalsBySerial[r.mr_serial_no] ?? 0).toFixed(2)
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MRs');
      XLSX.writeFile(wb, 'MR_export_summary.xlsx');
      return;
    }

    // Detailed: export in the DB-aligned template format with items listed
    const rows = filteredRows || [];
    if (rows.length === 0) { alert('No data to export'); return; }
    const headers = [
      'mr_serial_no','is_chargeable','vendor_name','mr_description','date_of_issue','currency','created_by','gst','ordered_by','approved_by','delivery_date','delivery_venue','delivery_recipient','delivery_hp','remarks','note','document_location','change_number','contract_from','contract_to','defects_liability_months','warranty_months','maintenance_service','item_no','item_description','item_quantity','item_measurement','buying_price','charging_price','delivery_charge','category_id'
    ];
    const out = [headers];
    for (const r of rows) {
      const locName = (() => {
        const id = r.document_location_id;
        const m = (locationOptions || []).find(l => l.document_location_id === id);
        return m ? (m.document_location || m.name || '') : '';
      })();
      try {
        const res = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(r.mr_serial_no)}/items`));
        const items = res.ok ? await res.json() : [];
        const headerVals = [
          r.mr_serial_no,
          r.is_chargeable ? 'Chargeable' : 'Non-Chargeable',
          r.vendor_name || '',
          r.mr_description || '',
          r.date_of_issue || '',
          r.currency || '',
          r.created_by || '',
          r.gst ?? '',
          r.ordered_by || '',
          r.approved_by || '',
          r.delivery_date || '',
          r.delivery_venue || '',
          r.delivery_recipient || '',
          r.delivery_hp || '',
        r.remarks || '',
        r.note || '',
        locName,
        r.change_number || '',
        r.contract_from || '',
          r.contract_to || '',
          r.defects_liability_months ?? '',
          r.warranty_months ?? '',
          r.maintenance_service || 'NA',
        ];
        if (items.length === 0) {
          out.push([...headerVals, '', '', '', '', '', '', '', '']);
        } else {
          for (const it of items) {
            out.push([...headerVals,
              it.item_no ?? '',
              it.item_description || '',
              it.item_quantity ?? '',
              it.item_measurement || '',
              it.buying_price ?? '',
              it.charging_price ?? '',
              it.delivery_charge ?? '',
              it.category_id ?? '',
            ]);
          }
        }
      } catch {
        out.push([
          r.mr_serial_no,
          r.is_chargeable ? 'Chargeable' : 'Non-Chargeable',
          r.vendor_name || '',
          r.mr_description || '',
          r.date_of_issue || '',
          r.currency || '',
          r.created_by || '',
          r.gst ?? '',
          r.ordered_by || '',
          r.approved_by || '',
          r.delivery_date || '',
          r.delivery_venue || '',
          r.delivery_recipient || '',
          r.delivery_hp || '',
        r.remarks || '',
        r.note || '',
        locName,
        r.change_number || '',
        r.contract_from || '',
          r.contract_to || '',
          r.defects_liability_months ?? '',
          r.warranty_months ?? '',
          r.maintenance_service || 'NA',
          '', '', '', '', '', '', '',
        ]);
      }
    }
    const ws = XLSX.utils.aoa_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MRs');
    XLSX.writeFile(wb, 'MR_export_detailed.xlsx');
    } catch (err) {
      alert('Export failed: ' + (err?.message || err));
    }
  };

  // Import from Excel (supports template and simple third-party mapping by header names)
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const tpl = wb.Sheets['Template'] || wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(tpl, { defval: '' });
      // Backward-compat: merge optional 'Items' sheet rows; importRows skips header upsert for item-only rows
      const itemsSheet = wb.Sheets['Items'];
      const legacyItems = itemsSheet ? XLSX.utils.sheet_to_json(itemsSheet, { defval: '' }) : [];
      await importRows([...(rows || []), ...(legacyItems || [])]);
    } catch (err) {
      setImportSummaryData({ total: 0, added: 0, overwritten: 0, skipped: 0, failed: 1, rows: [{ label: 'Import error', status: `Failed: ${err.message || 'Import failed'}` }] });
      setImportSummaryOpen(true);
    }
  };

  // Delete all MRs for this project (requires permission)
  const deleteAll = async () => {
    if (deleteAllAccess !== 'write') return;
    if (!projectId) return;
    if (!window.confirm('Delete ALL MRs for this project? This cannot be undone.')) return;
    try {
      // delete only for current project rows
      const rows = existing || [];
      const sess = (() => { try { return JSON.parse(sessionStorage.getItem('session') || localStorage.getItem('session') || '{}'); } catch { return {}; } })();
      const userIdResolved = userId || (sess && sess.user && sess.user.user_id);
      let success = 0, failed = 0;
      const errs = [];
      for (const r of rows) {
        const resp = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(r.mr_serial_no)}`), {
          method: 'DELETE',
          headers: { ...(userIdResolved ? { 'X-User-ID': userIdResolved } : {}) }
        });
        if (resp.ok) {
          success += 1;
        } else {
          failed += 1;
          try { const data = await resp.json(); if (data && data.error) errs.push(`${r.mr_serial_no}: ${data.error}`); } catch {}
        }
      }
      fetchExisting();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      if (failed === 0) {
        alert('All MRs deleted');
      } else {
        alert(`Deleted ${success}, failed ${failed}. You may lack write access for Material Requisitions or some records could not be deleted.`);
        if (errs.length) console.warn('Delete errors:', errs);
      }
    } catch (e) {
      alert('Delete failed');
    }
  };

  // Import dialogs UI
  const importSourceDialog = (
    <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
      <DialogTitle>Import From Excel</DialogTitle>
      <DialogContent>
        Choose source type:
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleImportSource(false)}>Template</Button>
        <Button onClick={() => handleImportSource(true)}>Third-party (Map Columns)</Button>
        <Button onClick={() => setImportSourceOpen(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  const mappingDialog = (
    <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Map Columns (Third-party Excel)</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 8 }}>
          <input type="file" accept=".xlsx,.xls" onChange={handleMapFileChange} />
        </div>
        {!!fileHeaders.length && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {MR_FIELDS.map(f => (
              <div key={f}>
                <div style={{ fontSize: 12, color: '#555' }}>{f}</div>
                <TextField select size="small" fullWidth value={mapping[f] || ''} onChange={handleMappingChange(f)}>
                  <MenuItem value="">(none)</MenuItem>
                  {fileHeaders.map(h => (
                    <MenuItem key={`${f}-${h}`} value={h}>{h}</MenuItem>
                  ))}
                </TextField>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
        <Button onClick={handleMappingSubmit} disabled={!mapFile}>Import</Button>
      </DialogActions>
    </Dialog>
  );

  const importingDialog = (
    <Dialog open={importing}>
      <DialogTitle>Importing...</DialogTitle>
      <DialogContent sx={{ minWidth: 300 }}>
        <LinearProgress variant="determinate" value={importProgress} />
        <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
      </DialogContent>
    </Dialog>
  );

  // Option lists that react to other filters
  const optSerials = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { serials: true }).map(r => r.mr_serial_no))];
  }, [existing, applyRowFilters]);
  const optTypes = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { types: true }).map(r => r.is_chargeable ? 'Chargeable' : 'Non-chargeable'))];
  }, [existing, applyRowFilters]);
  const optVendors = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { vendors: true }).map(r => r.vendor_name || ''))].filter(Boolean);
  }, [existing, applyRowFilters]);
  const optDescs = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { descs: true }).map(r => r.mr_description || ''))].filter(Boolean);
  }, [existing, applyRowFilters]);
  const optIssued = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { issued: true }).map(r => r.date_of_issue || ''))].filter(Boolean);
  }, [existing, applyRowFilters]);
  const optReqBy = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { reqby: true }).map(r => r.ordered_by || ''))].filter(Boolean);
  }, [existing, applyRowFilters]);
  const optApprBy = useMemo(() => {
    return [...new Set(applyRowFilters(existing, { apprby: true }).map(r => r.approved_by || ''))].filter(Boolean);
  }, [existing, applyRowFilters]);

  const totals = useMemo(() => {
    const toNum = v => (v === '' || v === null || v === undefined) ? 0 : +v;
    const cost = items.reduce((s, r) => s + toNum(r.item_quantity) * toNum(r.buying_price), 0);
    const bill = items.reduce((s, r) => s + toNum(r.item_quantity) * toNum(r.charging_price), 0);
    const delv = items.reduce((s, r) => s + toNum(r.delivery_charge), 0);
    const chg = header.is_chargeable;
    const gstPct = chg ? (parseFloat(header.gst) || 0) : 0;
    const gstAmt = chg ? (bill + delv) * gstPct / 100 : 0;
    const gross = chg ? (bill + delv + gstAmt) : (cost + delv);
    return { cost, bill, delv, gstPct, gstAmt, gross };
  }, [items, header.is_chargeable, header.gst]);

  const canEdit = access === 'write';
  const allowEdit = canEdit && !isViewMode; // inline editor: allow whenever user has write access (not in view mode)

  const fetchExisting = useCallback(() => {
    if (!projectId) return;
    setLoading(true); // Start loading
    fetch(apiUrl(`/api/material-requisitions?project_id=${projectId}`))
      .then(r => r.json())
      .then(async rows => {
        setExisting(rows || []);
        // compute totals per MR (no GST):
        const entries = await Promise.all(
          (rows || []).map(async r => {
            try {
              const res = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(r.mr_serial_no)}/items`));
              const items = res.ok ? await res.json() : [];
              const toNum = v => (v === '' || v === null || v === undefined) ? 0 : +v;
              const cost = items.reduce((s, it) => s + toNum(it.item_quantity) * toNum(it.buying_price), 0);
              const bill = items.reduce((s, it) => s + toNum(it.item_quantity) * toNum(it.charging_price), 0);
              const delv = items.reduce((s, it) => s + toNum(it.delivery_charge), 0);
              const totalNoGST = (r.is_chargeable ? (bill + delv) : (cost + delv));
              return [r.mr_serial_no, totalNoGST];
            } catch {
              return [r.mr_serial_no, 0];
            }
          })
        );
        const map = {};
        entries.forEach(([k, v]) => { map[k] = v; });
        setTotalsBySerial(map);
        setLoading(false); // Done loading
      })
      .catch(() => {
        setExisting([]);
        setLoading(false); // Done loading (even on error)
      });

    // Fetch all serials globally (no project filter)
    fetch(apiUrl('/api/material-requisitions'))
      .then(r => r.json())
      .then(rows => setAllSerials((rows || []).map(x => x.mr_serial_no)))
      .catch(() => setAllSerials([]));
  }, [projectId]);
  // Load document locations for current project
  useEffect(() => {
    if (!projectId) { setLocationOptions([]); return; }
    fetch(apiUrl(`/api/document-locations?project_id=${projectId}`))
      .then(r => r.json())
      .then(rows => setLocationOptions((rows || []).map(l => ({ ...l, document_location_id: l.document_location_id ?? l.id }))))
      .catch(() => setLocationOptions([]));
  }, [projectId]);

  // Fetch company data
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        setCompanyDataLoading(true);

        // Fetch current project to get company_id
        const projectRes = await fetch(apiUrl(`/api/projects/${projectId}`), {
          credentials: 'include'
        });

        if (!projectRes.ok) {
          setCompanyDataLoading(false);
          return;
        }

        const currentProject = await projectRes.json();

        if (!currentProject.company_id) {
          setCompanyDataLoading(false);
          return;
        }

        // Fetch company details
        const response = await fetch(apiUrl(`/api/companies/${currentProject.company_id}`), {
          credentials: 'include'
        });

        if (!response.ok) {
          setCompanyDataLoading(false);
          return;
        }

        const data = await response.json();
        setCompanyData({
          company_name: data.company_name || 'Wah Loon Plant & Instrumentation Pte Ltd',
          logo_url: data.logo_url || '',
          address: data.address || '10 Tagore Drive, Wah Loon Building, Singapore 787625',
          number_format: data.number_format || '1,234.56'
        });
        setCompanyDataLoading(false);
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompanyDataLoading(false);
      }
    };

    if (projectId) {
      fetchCompanyData();
    }
  }, [projectId]);

  const locationName = useMemo(() => {
    const id = header.document_location_id;
    if (!id) return '';
    const m = (locationOptions || []).find(l => l.document_location_id === id);
    return m ? (m.document_location || m.name || '') : '';
  }, [header.document_location_id, locationOptions]);

  useEffect(() => {
    setHeader(h => ({ ...h, project_id: projectId }));
    if (access !== 'none') fetchExisting();
  }, [projectId, access, fetchExisting]);

  // Hooks must be declared before any early returns
  const serialsGlobal = useMemo(() => new Set(allSerials), [allSerials]);
  const serialValidity = useMemo(() => {
    const s = (header.mr_serial_no || '').trim();
    if (!/^[A-Za-z][0-9]{5}$/.test(s)) return { ok: false, msg: 'Invalid format' };
    if (serialsGlobal.has(s) && s !== selectedSerial) return { ok: false, msg: 'Already exists' };
    return { ok: true, msg: 'OK' };
  }, [header.mr_serial_no, serialsGlobal, selectedSerial]);

  const nextSerial = (prefix) => {
    const p = (prefix || 'A').toUpperCase().slice(0, 1);
    let max = 0;
    (allSerials || []).forEach(s => {
      if (s && typeof s === 'string' && s.startsWith(p)) {
        const n = parseInt(s.slice(1), 10);
        if (!isNaN(n)) max = Math.max(max, n);
      }
    });
    return `${p}${String(max + 1).padStart(5, '0')}`;
  };

  // Derive Job Reference from selected project and keep read-only
  const currentProject = useMemo(() => projects.find(p => p.project_id === projectId), [projects, projectId]);
  const jobRef = currentProject?.project_job_reference || '';
  useEffect(() => {
    setHeader(h => (h.project_job_reference === jobRef ? h : { ...h, project_job_reference: jobRef }));
  }, [jobRef]);

  // Fetch design change options for dropdown (only unlinked DCs)
  useEffect(() => {
    if (!projectId) {
      setDesignChangeOptions([]);
      return;
    }
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}&exclude_linked=true`))
      .then(r => r.json())
      .then(rows => setDesignChangeOptions((rows || []).map(x => x.change_number)))
      .catch(() => setDesignChangeOptions([]));
  }, [projectId]);

  // Fetch contract options for dropdown (only Approved VOs - variation orders)
  useEffect(() => {
    if (!projectId) {
      setContractOptions([]);
      return;
    }
    fetch(apiUrl(`/api/contracts?project_id=${projectId}&contract_type=variation_order&status=Approved`))
      .then(r => r.json())
      .then(rows => {
        const contractOptions = (rows || []).map(x => ({
          id: x.contract_id,
          label: x.contract_number || x.title || `VO ${x.contract_id}`,
          number: x.contract_number,
          title: x.title
        }));
        setContractOptions(contractOptions);
      })
      .catch(() => setContractOptions([]));
  }, [projectId]);

  // Combine design changes and contracts into budget options
  useEffect(() => {
    const combined = [
      ...designChangeOptions.map(dc => ({ label: `DC: ${dc}`, value: dc, type: 'design_change' })),
      ...contractOptions.map(contract => ({ 
        label: `Contract: ${contract.label}`, 
        value: contract.id, 
        contractData: contract,
        type: 'contract' 
      }))
    ];
    setBudgetOptions(combined);
  }, [designChangeOptions, contractOptions]);

  // Fetch BOQ line items and enrich with prices from rate sheet (same as PO)
  useEffect(() => {
    if (!projectId) {
      setBoqLineItems([]);
      return;
    }

    fetch(apiUrl(`/api/budget-line-items?project_id=${projectId}`))
      .then(res => res.json())
      .then(budgetItems => {
        const items = Array.isArray(budgetItems) ? budgetItems : [];

        // Fetch active rate sheet to get prices
        fetch(apiUrl(`/api/rate-sheets?project_id=${projectId}`))
          .then(res => res.json())
          .then(rateSheets => {
            const activeSheet = Array.isArray(rateSheets)
              ? rateSheets.find(rs => rs.is_active) || rateSheets[0]
              : null;

            if (activeSheet) {
              // Fetch rate sheet items to get prices
              fetch(apiUrl(`/api/rate-sheets/${activeSheet.rate_sheet_id}/items`))
                .then(res => res.json())
                .then(rateItems => {
                  const rateItemsArray = Array.isArray(rateItems) ? rateItems : [];

                  // Enrich budget items with prices
                  const enrichedItems = items.map(item => {
                    const rateItem = rateItemsArray.find(ri => ri.bli_id === item.bli_id);
                    return {
                      ...item,
                      unit_price: rateItem ? rateItem.unit_price : 0
                    };
                  });

                  setBoqLineItems(enrichedItems);
                })
                .catch(() => {
                  setBoqLineItems(items.map(item => ({ ...item, unit_price: 0 })));
                });
            } else {
              setBoqLineItems(items.map(item => ({ ...item, unit_price: 0 })));
            }
          })
          .catch(() => {
            setBoqLineItems(items.map(item => ({ ...item, unit_price: 0 })));
          });
      })
      .catch(() => setBoqLineItems([]));
  }, [projectId]);

  // Fetch Budget Categories for project
  useEffect(() => {
    if (!projectId) {
      setBudgetCategories([]);
      return;
    }
    fetch(apiUrl(`/api/budget-categories?project_id=${projectId}`))
      .then(r => r.json())
      .then(rows => {
        console.log('Budget categories loaded:', rows);
        setBudgetCategories(rows || []);
      })
      .catch((err) => {
        console.error('Failed to load budget categories:', err);
        setBudgetCategories([]);
      });
  }, [projectId]);

  // Derived preview field: Job Site (Project - Location - Description)
  const jobSite = useMemo(() => {
    const projName = currentProject?.project_name || '';
    const loc = locationName || '';
    const desc = header.mr_description2 || header.mr_description || '';
    return [projName, loc, desc].filter(Boolean).join(' - ');
  }, [currentProject, locationName, header.mr_description, header.mr_description2]);

  const loadMR = async (serial) => {
    try {
      const res = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}?include_items=true`));
      if (!res.ok) throw new Error('Failed to load MR');
      const data = await res.json();
      setHeader(h => ({
        ...h,
        mr_serial_no: data.mr_serial_no || '',
        is_chargeable: !!data.is_chargeable,
        status: data.status || 'Draft',
        mr_type: data.mr_type || null,
        date_of_issue: data.date_of_issue || '',
        currency: data.currency || '',
        gst: data.gst ?? '',
        project_id: data.project_id || projectId,
        mr_description: data.mr_description || '',
        vendor_name: data.vendor_name || '',
        remarks: data.remarks || '',
        note: data.note || '',
        delivery_venue: data.delivery_venue || '',
        delivery_recipient: data.delivery_recipient || '',
        delivery_hp: data.delivery_hp || '',
        delivery_date: data.delivery_date || '',
        ordered_by: data.ordered_by || '',
        approved_by: data.approved_by || '',
        contract_from: data.contract_from || '',
        contract_to: data.contract_to || '',
        defects_liability_months: data.defects_liability_months ?? '',
        warranty_months: data.warranty_months ?? '',
        maintenance_service: data.maintenance_service || 'NA',
        created_by: data.created_by || '',
        design_change: data.change_number || '',
        vo_id: data.vo_id || null,
        contract_id: data.contract_id || null,
        document_location_id: data.document_location_id || null,
        rejection_comments: data.rejection_comments || '',
        po_for: data.po_for || '',
      }));

      // Fetch MR For options if mr_type exists
      if (data.mr_type && projectId) {
        fetchMrForOptions(data.mr_type, projectId);
      }
      const its = (data.items || []).map(it => ({
        item_no: it.item_no,
        item_description: it.item_description || '',
        item_quantity: it.item_quantity ?? 1,
        item_measurement: it.item_measurement || 'lot',
        buying_price: it.buying_price ?? 0,
        charging_price: it.charging_price ?? 0,
        delivery_charge: it.delivery_charge ?? 0,
        category_id: it.category_id || null,
        bli_id: it.bli_id || null,
        rsi_id: it.rsi_id || null,
        pm_comment: it.pm_comment || null,
      }));
      const itsWithId = its.map(it => ({ ...it, _id: it.item_no }));
      setItems(itsWithId.length ? itsWithId : [{ _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }]);
      setMrItemRowModesModel({});
      setSelectedSerial(serial);
      
      // Set budget selection based on loaded data
      setTimeout(() => {
        if (data.change_number && designChangeOptions.includes(data.change_number)) {
          setHeader(h => ({ 
            ...h, 
            budget_selection: { 
              label: `DC: ${data.change_number}`, 
              value: data.change_number, 
              type: 'design_change' 
            }
          }));
        } else if (data.contract_id && contractOptions.length > 0) {
          const contract = contractOptions.find(c => c.id === data.contract_id);
          if (contract) {
            setHeader(h => ({ 
              ...h, 
              budget_selection: { 
                label: `Contract: ${contract.label}`, 
                value: contract.id, 
                contractData: contract,
                type: 'contract' 
              }
            }));
          }
        }
      }, 100); // Small delay to ensure options are loaded
      
      // inline editing: no mode toggle
      return true;
    } catch (e) {
      alert(e.message || 'Failed to load MR');
      return false;
    }
  };

  const editMR = async (serial) => {
    const ok = await loadMR(serial);
    if (ok) {
      // inline editing: no mode toggle
    }
  };

  // Duplicate MR function removed - no longer needed
  // const duplicateMR = async (serial) => {
  //   try {
  //     const res = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(serial)}?include_items=true`));
  //     if (!res.ok) throw new Error('Failed to load MR');
  //     const data = await res.json();
  //     const origSerial = data.mr_serial_no || '';
  //     const newSerial = nextSerial(origSerial.slice(0, 1));
  //     setHeader(h => ({
  //       ...h,
  //       mr_serial_no: newSerial,
  //       is_chargeable: !!data.is_chargeable,
  //       mr_type: data.mr_type || null,
  //       date_of_issue: data.date_of_issue || '',
  //       currency: data.currency || '',
  //       gst: data.gst ?? '',
  //       project_id: projectId,
  //       mr_description: data.mr_description || '',
  //       vendor_name: data.vendor_name || '',
  //       remarks: data.remarks || '',
  //       note: data.note || '',
  //       delivery_venue: data.delivery_venue || '',
  //       delivery_recipient: data.delivery_recipient || '',
  //       delivery_hp: data.delivery_hp || '',
  //       delivery_date: data.delivery_date || '',
  //       ordered_by: data.ordered_by || '',
  //       approved_by: data.approved_by || '',
  //       contract_from: data.contract_from || '',
  //       contract_to: data.contract_to || '',
  //       defects_liability_months: data.defects_liability_months ?? '',
  //       warranty_months: data.warranty_months ?? '',
  //       maintenance_service: data.maintenance_service || 'NA',
  //       created_by: data.created_by || '',
  //       design_change: data.change_number || '',
  //       vo_id: data.vo_id || null,
  //       contract_id: data.contract_id || null,
  //       document_location_id: data.document_location_id || null,
  //     }));
  //     if (data.mr_type && projectId) {
  //       fetchMrForOptions(data.mr_type, projectId);
  //     }
  //     const its = (data.items || []).map(it => ({
  //       item_no: it.item_no,
  //       item_description: it.item_description || '',
  //       item_quantity: it.item_quantity ?? 1,
  //       item_measurement: it.item_measurement || 'lot',
  //       buying_price: it.buying_price ?? 0,
  //       charging_price: it.charging_price ?? 0,
  //       delivery_charge: it.delivery_charge ?? 0,
  //       category_id: it.category_id || null,
  //     }));
  //     setItems(its.length ? its : [{ item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null }]);
  //     setSelectedSerial(null);
  //   } catch (e) {
  //     alert(e.message || 'Failed to duplicate MR');
  //   }
  // };

  // Open delete confirmation dialog
  const handleDelete = (serial) => {
    setMrToDelete(serial);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMrToDelete(null);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    if (!mrToDelete) return;

    try {
      const r = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(mrToDelete)}`), {
        method: 'DELETE',
        headers: { ...(userId ? { 'X-User-ID': userId } : {}) },
        credentials: 'include'
      });
      if (!r.ok) throw new Error('Delete failed');
      if (selectedSerial === mrToDelete) {
        // Clear selection and form
        setSelectedSerial(null);
        setHeader(h => ({
          ...h,
          mr_serial_no: '',
          is_chargeable: false,
          mr_type: null,
          date_of_issue: '',
          currency: '',
          gst: '',
          mr_description: '',
          vendor_name: '',
          remarks: '',
          delivery_venue: '',
          delivery_recipient: '',
          delivery_hp: '',
          delivery_date: '',
          ordered_by: '',
          approved_by: '',
          contract_from: '',
          contract_to: '',
          defects_liability_months: '',
          warranty_months: '',
          maintenance_service: 'NA',
          created_by: '',
          design_change: '',
          vo_id: null,
          contract_id: null,
          budget_selection: null
        }));
        setMrForOptions([]); // Clear MR For options
        setItems([{ _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }]);
      }
      fetchExisting();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      handleCloseDeleteDialog();
    } catch (e) {
      console.error('Error deleting MR:', e);
      alert(e.message || 'Delete failed');
      handleCloseDeleteDialog();
    }
  };

  if (access === 'none') return <div>Access denied</div>;
  if (!projectId) return <div>Please select a project.</div>;

  const autoSerial = () => {
    const prefix = (header.mr_serial_no?.slice(0, 1) || 'A').toUpperCase();
    let max = 0;
    (allSerials || []).forEach(s => {
      if (s && typeof s === 'string' && s.startsWith(prefix)) {
        const n = parseInt(s.slice(1), 10);
        if (!isNaN(n)) max = Math.max(max, n);
      }
    });
    const next = `${prefix}${String(max + 1).padStart(5, '0')}`;
    setHeader(h => ({ ...h, mr_serial_no: next }));
  };

  const serialHint = () => {
    const s = (header.mr_serial_no || '').trim();
    if (!s) return '';
    const ok = /^[A-Za-z][0-9]{5}$/.test(s);
    if (!ok) return 'Must be 1 letter + 5 digits (e.g., A00001).';
    if (serialsGlobal.has(s) && s !== selectedSerial) return 'Serial already exists.';
    return 'Looks good.';
  };

  const addItem = () => {
    const tempId = `temp_${Date.now()}`;
    const nextNo = (items[items.length - 1]?.item_no || 0) + 1;
    const newItem = { _id: tempId, item_no: nextNo, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null };
    setItems(prev => [...prev, newItem]);
    setMrItemRowModesModel(prev => ({
      ...prev,
      [tempId]: { mode: GridRowModes.Edit, fieldToFocus: 'item_description' }
    }));
  };

  const delItem = (item_no) => setItems(prev => prev.filter(r => r.item_no !== item_no).map((r, i) => ({ ...r, item_no: i + 1 })));

  const updateItem = (item_no, patch) => setItems(prev => prev.map(r => r.item_no === item_no ? { ...r, ...patch } : r));

  // MR DataGrid item handlers
  const handleMrItemEditClick = (id: any) => () => {
    setMrItemRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
  };

  const handleMrItemSaveClick = (id: any) => () => {
    setMrItemRowModesModel(prev => ({ ...prev, [id]: { mode: GridRowModes.View } }));
  };

  const handleMrItemCancelClick = (id: any) => () => {
    setMrItemRowModesModel(prev => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    }));
    if (String(id).startsWith('temp_')) {
      setItems(prev => prev.filter(item => (item as any)._id !== id));
    }
  };

  const handleMrItemDeleteClick = (id: any) => () => {
    setItems(prev => prev.filter(item => ((item as any)._id || item.item_no) !== id).map((r, i) => ({ ...r, item_no: i + 1 })));
  };

  const processMrItemRowUpdate = (newRow: any) => {
    setItems(prev => prev.map(item => {
      const rowId = (item as any)._id || item.item_no;
      if (rowId === newRow._id || rowId === newRow.item_no) {
        return { ...item, ...newRow };
      }
      return item;
    }));
    return newRow;
  };

  const handleMrItemRowModesModelChange = (newModel: GridRowModesModel) => {
    setMrItemRowModesModel(newModel);
  };

  const handleMrItemRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const saveAll = async () => {
    if (!canEdit) return;
    const s = serialHint();
    if (s && s !== 'Looks good.') {
      alert(`Serial invalid: ${s}`);
      return;
    }
    // Validate duplicate item numbers locally to avoid server 500s
    try {
      const counts = new Map();
      for (const it of items || []) {
        const n = Number(it.item_no);
        if (Number.isInteger(n) && n > 0) counts.set(n, (counts.get(n) || 0) + 1);
      }
      const dups = Array.from(counts.entries()).filter(([, c]) => c > 1).map(([n]) => n);
      if (dups.length) {
        // @ts-ignore
        const cn = header.design_change || header.change_number || '';
        alert(`Duplicate item numbers detected${cn ? ` for change ${cn}` : ''}: ${dups.join(', ')}. Please fix the Excel/table and retry.`);
        return;
      }
    } catch {}
    setSaving(true);
    try {
      // If editing an Approved MR, revert to Draft first (also deletes linked PO)
      if (editingApprovedMR) {
        const revertRes = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(selectedSerial || header.mr_serial_no)}/status`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: 'Draft' })
        });
        if (!revertRes.ok) {
          const err = await revertRes.json();
          throw new Error(err.error || 'Cannot edit this MR - its Purchase Order may already be approved');
        }
        setEditingApprovedMR(false);
      }

      // Create or update header based on presence
      const normalizeMaint = (v) => {
        const s = String(v || '').trim().toLowerCase();
        if (!s) return 'NA';
        if (['monthly'].includes(s)) return 'Monthly';
        if (['bi-monthly','bi monthly','bimonthly','bi monthly.','bi-monthly.'].includes(s)) return 'Bi-Monthly';
        if (['quarterly'].includes(s)) return 'Quarterly';
        if (['yearly','annually','annual'].includes(s)) return 'Yearly';
        if (['na','n.a.','n/a','none'].includes(s)) return 'NA';
        return 'NA';
      };
      const payload = { ...header, maintenance_service: normalizeMaint(header.maintenance_service) };
      // Clean preview-only fields
      delete payload.project_job_reference;
      delete payload.mr_description2;
      delete payload.budget_selection;
      // @ts-ignore
      payload.change_number = header.design_change || null;
      payload.contract_id = header.contract_id || null;
      payload.mr_type = header.mr_type || null;
      payload.vo_id = header.vo_id || null;

      // Decide upsert method considering duplicates across projects
      const existsHere = (existing || []).some(r => r.mr_serial_no === header.mr_serial_no);
      const allSerialsSet = new Set(allSerials || []);
      const existsElsewhere = allSerialsSet.has(header.mr_serial_no) && !existsHere;
      if (existsElsewhere) {
        throw new Error(`Serial ${header.mr_serial_no} exists in another project. Choose a different serial or switch to that project.`);
      }
      const isEditingExisting = !!selectedSerial;
      let upsert = isEditingExisting || existsHere ? 'PUT' : 'POST';
      const url = upsert === 'POST'
        ? apiUrl('/api/material-requisitions')
        : apiUrl(`/api/material-requisitions/${encodeURIComponent(selectedSerial || header.mr_serial_no)}`);
      const bodyToSend = (upsert === 'PUT') ? (({ project_id, ...rest }) => rest)(payload) : payload;
      let res = await fetch(url, {
        method: upsert,
        headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
        credentials: 'include',
        body: JSON.stringify(bodyToSend)
      });
      if (!res.ok) {
        // Fallback: if POST failed due to existing serial, try to switch to PUT when it belongs here
        if (upsert === 'POST') {
          const chk = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(header.mr_serial_no)}`), { credentials: 'include' });
          if (chk.ok) {
            const hdr = await chk.json();
            if (hdr && hdr.project_id === projectId) {
              upsert = 'PUT';
              const putUrl = `apiUrl('/api/material-requisitions/${encodeURIComponent(header.mr_serial_no)}`;
              res = await fetch(putUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) }, credentials: 'include', body: JSON.stringify((({ project_id, ...rest }) => rest)(payload)) });
            } else {
              throw new Error(`Failed to save MR header. Note: Serial ${header.mr_serial_no} exists in another project.`);
            }
          }
        }
        if (!res.ok) throw new Error('Failed to save MR header');
      }

      // If created, ensure header exists before posting items
      if (upsert === 'POST') {
        const created = await res.json();
        setHeader(h => ({ ...h, mr_serial_no: created.mr_serial_no }));
        setSelectedSerial(created.mr_serial_no);
      }

      // Save items in bulk (replace all)
      const payloadItems = (items || []).map(it => ({
        item_no: it.item_no,
        item_description: it.item_description || '',
        // @ts-ignore
        item_quantity: it.item_quantity === '' ? '' : Number(it.item_quantity || 0),
        item_measurement: it.item_measurement || 'lot',
        // @ts-ignore
        buying_price: it.buying_price === '' ? '' : Number(it.buying_price || 0),
        currency: header.currency || undefined,
        // @ts-ignore
        charging_price: header.is_chargeable ? (it.charging_price === '' ? '' : Number(it.charging_price || 0)) : null,
        // @ts-ignore
        delivery_charge: it.delivery_charge === '' ? '' : Number(it.delivery_charge || 0),
        category_id: it.category_id || null,
        bli_id: it.bli_id || null,
      }));
      const bulkRes = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(header.mr_serial_no)}/items/bulk`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(userId ? { 'X-User-ID': userId } : {}) },
        credentials: 'include',
        body: JSON.stringify({ items: payloadItems })
      });
      if (!bulkRes.ok) {
        let msg = 'Failed to save items';
        try { const data = await bulkRes.json(); if (data && data.error) { msg = data.error; if (data.duplicates && data.duplicates.length) msg += ` Duplicates: ${data.duplicates.join(', ')}`; } } catch {}
        throw new Error(msg);
      }
      fetchExisting();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
      // inline editing: no mode toggle
      setSnackbar({
        open: true,
        message: selectedSerial ? 'Material Requisition updated successfully!' : 'Material Requisition created successfully!',
        severity: 'success'
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.message || 'Save failed',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };


  const chg = header.is_chargeable;

  // MR Description Autocomplete edit cell
  const MrDescriptionEditCell = (params: GridRenderEditCellParams) => {
    const itemsToShow = (() => {
      const row = items.find(item => ((item as any)._id || item.item_no) === params.id);
      const catId = row?.category_id;
      const all = Array.isArray(boqLineItems) ? boqLineItems : [];
      return catId ? all.filter((item: any) => item.category_id === catId) : all;
    })();

    return (
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        options={itemsToShow}
        getOptionLabel={(option: any) => typeof option === 'string' ? option : option.item_description || ''}
        value={params.value || ''}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newValue });
          } else if (newValue) {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newValue.item_description });
            setTimeout(() => {
              params.api.setEditCellValue({ id: params.id, field: 'buying_price', value: newValue.unit_price || 0 });
              params.api.setEditCellValue({ id: params.id, field: 'item_measurement', value: newValue.unit_code || 'lot' });
              params.api.setEditCellValue({ id: params.id, field: 'category_id', value: newValue.category_id || null });
            }, 100);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            params.api.setEditCellValue({ id: params.id, field: 'item_description', value: newInputValue });
          }
        }}
        renderOption={(props, option: any) => {
          const { key, ...otherProps } = props;
          return (
            <Box component="li" key={key} {...otherProps} sx={{ display: 'block', py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {option.item_description || option.item_code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.category_name} • {option.unit_code} • ${option.unit_price || 0}
              </Typography>
            </Box>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          return options.filter((option: any) =>
            option.item_description?.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.category_name?.toLowerCase().includes(inputValue.toLowerCase())
          );
        }}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            variant="standard"
            placeholder="Select or type"
            sx={{ '& .MuiInput-root': { fontSize: '0.875rem' } }}
          />
        )}
        sx={{ width: '100%', '& .MuiAutocomplete-input': { minWidth: '100px !important' } }}
      />
    );
  };

  // Check if any MR item has PM comment
  const mrHasPmComments = items.some(item => (item as any).pm_comment);

  // MR Items DataGrid columns
  const mrItemColumns: GridColDef[] = [
    {
      field: 'rowNum',
      headerName: '#',
      width: 40,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const index = items.findIndex(item => ((item as any)._id || item.item_no) === params.id);
        return String(index + 1).padStart(2, '0');
      }
    },
    {
      field: 'item_description',
      headerName: 'Description',
      flex: 2,
      minWidth: 180,
      editable: allowEdit,
      renderEditCell: (params) => <MrDescriptionEditCell {...params} />
    },
    {
      field: 'category_id',
      headerName: 'Category',
      width: 120,
      editable: allowEdit,
      type: 'singleSelect',
      valueOptions: (Array.isArray(budgetCategories) ? budgetCategories : []).map((cat: any) => ({ value: cat.category_id, label: cat.name })),
      renderCell: (params) => {
        const cat = (Array.isArray(budgetCategories) ? budgetCategories : []).find((c: any) => c.category_id === params.value);
        return cat ? cat.name : '';
      }
    },
    {
      field: 'item_quantity',
      headerName: 'Qty',
      width: 60,
      type: 'number',
      editable: allowEdit,
      align: 'right',
      headerAlign: 'right'
    },
    {
      field: 'buying_price',
      headerName: 'Buying',
      width: 100,
      type: 'number',
      editable: allowEdit,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value || 0, header.currency || 'SGD', companyData.number_format)
    },
    ...(chg ? [{
      field: 'charging_price',
      headerName: 'Charging',
      width: 100,
      type: 'number' as const,
      editable: allowEdit,
      align: 'right' as const,
      headerAlign: 'right' as const,
      renderCell: (params: GridRenderCellParams) => formatCurrency(params.value || 0, header.currency || 'SGD', companyData.number_format)
    }] : []),
    {
      field: 'delivery_charge',
      headerName: 'Delivery',
      width: 90,
      type: 'number',
      editable: allowEdit,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => formatCurrency(params.value || 0, header.currency || 'SGD', companyData.number_format)
    },
    ...(mrHasPmComments || selectedSerial ? [{
      field: 'pm_comment',
      headerName: 'PM Comment',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        <span style={{ color: params.value ? '#222' : '#bbb', fontStyle: params.value ? 'normal' : 'italic', fontSize: 13 }}>
          {params.value || '-'}
        </span>
      )
    }] : []),
    ...(!isViewMode ? [{
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 80,
      cellClassName: 'actions',
      getActions: ({ id }: { id: any }) => {
        const isInEditMode = mrItemRowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              color="primary"
              onClick={handleMrItemSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleMrItemCancelClick(id)}
              color="inherit"
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleMrItemEditClick(id)}
            color="inherit"
            disabled={!allowEdit}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleMrItemDeleteClick(id)}
            color="inherit"
            disabled={!allowEdit}
          />
        ];
      }
    }] : []),
  ];

  // Prepare MR items rows with unique IDs
  const mrItemRows = items.map((item, index) => ({
    ...item,
    _id: (item as any)._id || item.item_no || `row_${index}`
  }));

  const handleSendForApproval = async (mr_serial_no: string) => {
    try {
      const response = await fetch(apiUrl(`/api/material-requisitions/${encodeURIComponent(mr_serial_no)}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'Pending Approval' })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to send MR for approval');
      }
      setSnackbar({ open: true, message: 'MR sent for approval successfully', severity: 'success' });
      fetchExisting();
      window.dispatchEvent(new CustomEvent('refresh-pending-counts'));
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Failed to send MR for approval', severity: 'error' });
    }
  };

  const handleAddMR = () => {
    // Reset form for new MR
    setHeader({
      mr_serial_no: '', is_chargeable: false, status: 'Draft', mr_type: null, date_of_issue: '', currency: '', gst: '', project_id: projectId,
      project_job_reference: '', mr_description: '', vendor_name: '', remarks: '', note: '',
      delivery_venue: '', delivery_recipient: '', delivery_hp: '', delivery_date: '', ordered_by: '', approved_by: '',
      contract_from: '', contract_to: '', defects_liability_months: '', warranty_months: '', maintenance_service: 'NA', created_by: '',
      document_location_id: null, mr_description2: '', design_change: null, contract_id: null, vo_id: null, budget_selection: null, rejection_comments: '',po_for: ''
    });
    setItems([{ _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }]);
    setSelectedSerial(null);
    setMrForOptions([]); // Clear MR For options
    setDialogOpen(true);
  };

  const handleEditMR = (row: any) => {
    if (row.status === 'Approved') {
      setSnackbar({
        open: true,
        message: 'This MR is already approved and cannot be edited.',
        severity: 'warning'
      });
      return;
    }
    // If MR is not Draft, revert to Draft on save so QS can resubmit for approval
    setEditingApprovedMR(row.status !== 'Draft');
    loadMR(row.mr_serial_no);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingApprovedMR(false);
    setIsViewMode(false);
  };

  // Handle View MR Details (eye button) - opens full form in read-only mode
  const handleViewMR = async (row: any) => {
    setIsViewMode(true);
    await loadMR(row.mr_serial_no);
    setDialogOpen(true);
  };

  const handleViewMRClose = () => {
    setViewMRDialog({ open: false, data: null, items: [], loadingItems: false });
  };

  // Handle View PO button click
  const handleViewPO = async (mrSerial) => {
    setLoadingPO(true);
    setPoDialogOpen(true);
    try {
      // Fetch PO by MR serial number
      const response = await fetch(apiUrl(`/api/purchase-orders?mr_serial_no=${mrSerial}`), {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // Fetch PO details with items
          const poResponse = await fetch(apiUrl(`/api/purchase-orders/${data[0].po_number}`), {
            credentials: 'include'
          });
          if (poResponse.ok) {
            const poData = await poResponse.json();
            setSelectedPO(poData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching PO:', error);
    } finally {
      setLoadingPO(false);
    }
  };

  const handleClosePODialog = () => {
    setPoDialogOpen(false);
    setSelectedPO(null);
  };

  // DataGrid column definitions
  const columns: GridColDef[] = [
    {
      field: 'mr_serial_no',
      headerName: 'MR Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            color: '#1976d2',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={() => loadMR(params.value)}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'po_for',
      headerName: 'PO Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'mr_type',
      headerName: 'MR Type',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Tooltip title={params.value === 'VO' ? 'Variation Order' : params.value}>
            <Chip
              label={params.value}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                bgcolor: params.value === 'Contract' ? '#e3f2fd' : params.value === 'VO' ? '#fff3e0' : '#f3e5f5',
                color: params.value === 'Contract' ? '#1976d2' : params.value === 'VO' ? '#f57c00' : '#7b1fa2',
                fontWeight: 600
              }}
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      )
    },
    {
      field: 'mr_for',
      headerName: 'Reference',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'vendor_name',
      headerName: 'Vendor',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'is_chargeable',
      headerName: 'Chargeable',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        return row.is_chargeable ? 'Yes' : 'No';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.is_chargeable ? 'Yes' : 'No'}
          size="small"
          color={params.row.is_chargeable ? 'success' : 'default'}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'date_of_issue',
      headerName: 'Date Issued',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'ordered_by',
      headerName: 'Requested By',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'approved_by',
      headerName: 'Approved By',
      flex: 1,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">{params.value || '-'}</Typography>
      )
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1.2,
      minWidth: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      valueGetter: (value, row) => {
        const total = totalsBySerial[row.mr_serial_no] ?? 0;
        return formatCurrency(total, row.currency || 'SGD', companyData.number_format);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'Draft'}>
          <Chip
            label={params.value || 'Draft'}
            size="small"
            color={
              params.value === 'Approved' ? 'success' :
              params.value === 'Rejected' ? 'error' :
              params.value === 'Pending Approval' ? 'warning' :
              'default'
            }
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 600 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const isDraft = params.row.status === 'Draft' || params.row.status === 'Rejected';
        // Approved MR is editable only if its PO is not yet Approved
        const isApprovedEditable = params.row.status === 'Approved' && params.row.po_status !== 'Approved';
        const canEditRow = isDraft || isApprovedEditable;

        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            {/* View button - always show */}
            <Tooltip title="View Details">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); handleViewMR(params.row); }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Edit button - hidden for Approved MRs (cannot be edited) */}
            {params.row.status !== 'Approved' && (
              <Tooltip title={isDraft ? 'Edit' : 'Edit (will reset to Draft)'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditMR(params.row);
                    }}
                    disabled={access !== 'write'}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Send for Approval - only for Draft */}
            {isDraft && (
              <Tooltip title="Send for Approval">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    onClick={(e) => { e.stopPropagation(); handleSendForApproval(params.row.mr_serial_no); }}
                    disabled={access !== 'write'}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Delete button - always show for all statuses */}
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(params.row.mr_serial_no);
                  }}
                  disabled={access !== 'write'}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, margin: '0 24px 16px 24px' }}>
        {access === 'write' && (
          <Button variant="contained" color="primary" onClick={handleAddMR}>
            + Add MR
          </Button>
        )}
      </div>


      {/* Filters Section */}
      <Accordion expanded={filtersOpen} onChange={() => setFiltersOpen(v => !v)} sx={{ mb: 2, mx: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Material Requisition Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <Autocomplete
              multiple
              options={optSerials}
              value={fSerials}
              onChange={(_,v)=>setFSerials(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="MR Serial No." placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optTypes}
              value={fTypes}
              onChange={(_,v)=>setFTypes(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Type" placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optVendors}
              value={fVendors}
              onChange={(_,v)=>setFVendors(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Vendor" placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optDescs}
              value={fDescs}
              onChange={(_,v)=>setFDescs(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Description" placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optIssued}
              value={fIssued}
              onChange={(_,v)=>setFIssued(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Issued" placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optReqBy}
              value={fReqBy}
              onChange={(_,v)=>setFReqBy(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Requested By" placeholder="Filter" size="small" />}
            />
            <Autocomplete
              multiple
              options={optApprBy}
              value={fApprBy}
              onChange={(_,v)=>setFApprBy(v)}
              renderOption={(props, option, {selected})=> (<li {...props}><Checkbox checked={selected} sx={{mr:1}} />{option}</li>)}
              renderInput={(p)=><TextField {...p} label="Approved By" placeholder="Filter" size="small" />}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={()=>{ setFSerials([]); setFTypes([]); setFVendors([]); setFDescs([]); setFIssued([]); setFReqBy([]); setFApprBy([]); }}
            >
              Clear All Filters
            </Button>
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Material Requisitions DataGrid */}
      <Box sx={{ mx: 3, minHeight: 300, maxHeight: 600, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', height: '100%' }}>
            {/* Header skeleton */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 120px', gap: 2, px: 2, py: 1.5, bgcolor: 'grey.100', borderBottom: '2px solid #e0e0e0' }}>
              {['MR Serial', 'Description', 'Vendor', 'Type', 'Status', 'Date', 'Total', 'Actions'].map((_, i) => (
                <Skeleton key={i} variant="text" height={24} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
            {/* Row skeletons */}
            {[...Array(7)].map((_, rowIdx) => (
              <Box key={rowIdx} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr 120px', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                <Skeleton variant="text" height={20} width="80%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" height={20} width="90%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" height={20} width="70%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" height={24} width={70} sx={{ borderRadius: 2 }} />
                <Skeleton variant="rounded" height={24} width={80} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" height={20} width="60%" sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" height={20} width="50%" sx={{ borderRadius: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="circular" width={28} height={28} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
        <StyledDataGrid
          rows={existing.filter(row => {
            const inSet = (arr, val) => !arr.length || arr.includes(val || '');
            return (
              inSet(fSerials, row.mr_serial_no) &&
              inSet(fTypes, row.is_chargeable ? 'Chargeable' : 'Non-chargeable') &&
              inSet(fVendors, row.vendor_name || '') &&
              inSet(fDescs, row.mr_description || '') &&
              inSet(fIssued, row.date_of_issue || '') &&
              inSet(fReqBy, row.ordered_by || '') &&
              inSet(fApprBy, row.approved_by || '')
            );
          })}
          columns={columns}
          getRowId={(row) => row.mr_serial_no}
          loading={loading}
          paginationMode="client"
          onRowClick={(params) => loadMR(params.row.mr_serial_no)}
          onRowDoubleClick={(params) => handleViewMR(params.row)}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
        )}
      </Box>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 8, margin: '16px 24px 8px 24px' }}>
        <Button size="small" variant="outlined" onClick={exportTemplate}>Export Template</Button>
        <Button size="small" variant="contained" onClick={exportFiltered}>Export To Excel</Button>
        <Button size="small" variant="contained" onClick={handleImportClick}>Import From Excel</Button>
        {deleteAllAccess === 'write' && (
          <Button size="small" color="error" variant="contained" onClick={deleteAll}>Delete All</Button>
        )}
        <input type="file" accept=".xlsx,.xls" style={{ display:'none' }} ref={fileInputRef} onChange={onImportFile} />
      </div>

      {/* MR Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isViewMode ? `View MR: ${header.mr_serial_no}` : (selectedSerial ? `Edit MR: ${selectedSerial}` : 'Create New MR')}
        </DialogTitle>
        <DialogContent>
          {/* Rejection Comments Banner (view and edit mode) */}
          {header.rejection_comments && (
            <Box sx={{
              p: 2.5,
              mb: 2,
              bgcolor: '#fff5f5',
              border: '2px solid #f44336',
              borderLeft: '6px solid #f44336',
              borderRadius: 2,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start'
            }}>
              <Box sx={{ color: '#f44336', mt: 0.2, fontSize: 20, fontWeight: 'bold', lineHeight: 1 }}>⚠</Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>
                  Manager Rejection Comments
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontWeight: 500 }}>
                  {header.rejection_comments}
                </Typography>
              </Box>
            </Box>
          )}
          <div style={{ paddingTop: 16 }}>
        {/* Single-screen editor; top header removed per request */}

        {/* Non-print MR general info (outside template) - hidden in view mode */}
        {!isViewMode && <Section>
          <div className="mr-no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 16, alignItems: 'end' }}>
            <Autocomplete
              options={locationOptions}
              size="small"
              getOptionLabel={(o) => (o?.document_location || o?.name || "")}
              value={locationOptions.find(l => l.document_location_id === header.document_location_id) || null}
              onChange={(_, v) => setHeader(h => ({ ...h, document_location_id: v ? v.document_location_id : null }))}
              renderInput={(params) => <TextField {...params} label="Location" size="small" variant="outlined" />}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              variant="outlined"
              value={header.mr_description2 || header.mr_description || ''}
              onChange={e => setHeader(h => ({ ...h, mr_description2: e.target.value, mr_description: e.target.value }))}
              placeholder="Short description"
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <TextField
                label="MR Serial No."
                size="small"
                variant="outlined"
                value={header.mr_serial_no || ''}
                onChange={e => setHeader(h => ({ ...h, mr_serial_no: e.target.value.toUpperCase() }))}
                placeholder="A00001"
                inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
                sx={{ flex: 1, minWidth: 120 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={autoSerial}
                sx={{ height: 40, minWidth: 100 }}
              >
                Generate
              </Button>
              <span style={{ fontSize: 11, color: serialValidity.ok ? '#4caf50' : '#f44336', marginTop: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {serialValidity.ok ? '✓ Valid' : `✕ ${serialValidity.msg}`}
              </span>
            </div>
          </div>
        </Section>}

        {/* Basics moved into preview */}
        {/* Items moved into preview */}
        {/* Delivery & People moved into preview */}
        {/* Terms moved into preview */}
        {/* Preview */}

        {/* Items step removed */}

        {/* Delivery & People step removed */}

        {/* Terms step removed */}

        <Section>
          <div className={`mr-preview${isViewMode ? ' mr-view-mode' : ''}`} style={{ background: 'white', color: '#222' }}>
            {/* Non-print controls inside the template area (top-right) - REMOVED, NOW IN DIALOG ACTIONS */}
            {!isViewMode && <div className="mr-no-print" style={{ display: 'none', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
              {canEdit && <button onClick={saveAll} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }} disabled={saving}>Save</button>}
              <button onClick={() => window.print()} style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }}>Print</button>
              <button style={{ ...fieldStyle, width: 'auto', cursor: 'pointer' }} onClick={() => { setHeader({
                mr_serial_no: '', is_chargeable: false, status: 'Draft', mr_type: null, date_of_issue: '', currency: '', gst: '', project_id: projectId,
                project_job_reference: '', mr_description: '', vendor_name: '', remarks: '', note: '',
                delivery_venue: '', delivery_recipient: '', delivery_hp: '', delivery_date: '', ordered_by: '', approved_by: '',
                contract_from: '', contract_to: '', defects_liability_months: '', warranty_months: '', maintenance_service: 'NA', created_by: '',
                document_location_id: null, mr_description2: '', design_change: null, vo_id: null, contract_id: null, budget_selection: null, rejection_comments: '',po_for:''
              }); setItems([{ _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }]); setSelectedSerial(null); setMrForOptions([]); }}>Reset</button>
            </div>}

              {/* Page Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {companyDataLoading ? (
                    <>
                      <Skeleton variant="rectangular" width={80} height={60} sx={{ marginRight: 1 }} />
                      <div style={{ flex: 1 }}>
                        <Skeleton variant="text" width="80%" height={30} sx={{ marginBottom: 0.5 }} />
                        <Skeleton variant="text" width="60%" height={20} />
                      </div>
                    </>
                  ) : (
                    <>
                      {companyData.logo_url && (
                        <div style={{ marginRight: 8 }}>
                          <img
                            src={companyData.logo_url}
                            alt={`${companyData.company_name} Logo`}
                            style={{
                              maxHeight: '60px',
                              maxWidth: '100px',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 20 }}>{companyData.company_name || 'Company Name'}</div>
                        <div style={{ color: '#6b7280' }}>{companyData.address || 'Address'}</div>
                      </div>
                    </>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>MATERIAL REQUISITION<br/>FORM</div>
                  <div style={{ color: '#6b7280', fontWeight: 700 }}>({header.is_chargeable ? 'CHARGEABLE' : 'NON-CHARGEABLE'})</div>
                  <div className="mr-no-print" style={{ marginTop: 4 }}>
                    <select disabled={!allowEdit} value={header.is_chargeable ? 'charge' : 'non'} onChange={e => setHeader(h => ({ ...h, is_chargeable: e.target.value === 'charge' }))} style={{ ...fieldStyle, width: 'auto' }}>
                      <option value="non">Non-chargeable</option>
                      <option value="charge">Chargeable</option>
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ height: 3, background: 'rgb(148,0,0)', margin: '10px 0 8px' }} />

              {/* Meta: Clean 2-column grid layout */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 10 }}>
                {/* Row 1 */}
                <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                  <b>Job Ref :</b> <span>{jobRef || '-'}</span>
                </div>
                <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                  <b>Serial No :</b> <input readOnly value={header.mr_serial_no} style={{ border: 'none', background: 'transparent', padding: 0, width: '100%' }} placeholder="A00001" />
                </div>

                {/* Row 2 */}
                <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                  <b>Job Site :</b> <input disabled value={jobSite} readOnly style={{ border: 'none', background: 'transparent', padding: 0, width: '100%' }} />
                </div>
                <div style={{ borderBottom: '1px solid #e5e7eb', padding: '6px 0' }}>
                  <b>Status :</b> <span style={{ fontWeight: 600, color: header.status === 'Approved' ? '#4caf50' : header.status === 'Rejected' ? '#f44336' : '#666' }}>
                    {header.status || 'Draft'}
                  </span>
                </div>

                {/* NEW: MR Type */}
                <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="MR Type"
                    disabled={!allowEdit}
                    value={header.mr_type || ''}
                    onChange={e => {
                      const newType = e.target.value || null;
                      setHeader(h => ({
                        ...h,
                        mr_type: newType,
                        // Clear related IDs when type changes
                        contract_id: null,
                        vo_id: null,
                        design_change: ''
                      }));
                      // Fetch options based on selected type
                      if (newType && projectId) {
                        fetchMrForOptions(newType, projectId);
                      } else {
                        setMrForOptions([]);
                      }
                    }}
                  >
                    <MenuItem value="">-- Select MR Type --</MenuItem>
                    <MenuItem value="Contract">Contract</MenuItem>
                    <MenuItem value="VO">VO</MenuItem>
                    <MenuItem value="Design Change">Design Change</MenuItem>
                  </TextField>
                </div>

                {/* NEW: MR For (Conditional) */}
                {header.mr_type && (
                  <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={`MR For (${header.mr_type})`}
                      disabled={!allowEdit || mrForLoading}
                      value={
                        header.mr_type === 'Contract' ? (header.contract_id || '') :
                        header.mr_type === 'VO' ? (header.vo_id || '') :
                        header.mr_type === 'Design Change' ? (header.design_change || '') : ''
                      }
                      onChange={e => {
                        const val = e.target.value;
                        if (header.mr_type === 'Contract') {
                          setHeader(h => ({ ...h, contract_id: val ? Number(val) : null, vo_id: null, design_change: '' }));
                        } else if (header.mr_type === 'VO') {
                          setHeader(h => ({ ...h, vo_id: val ? Number(val) : null, contract_id: null, design_change: '' }));
                        } else if (header.mr_type === 'Design Change') {
                          setHeader(h => ({ ...h, design_change: val || '', contract_id: null, vo_id: null }));
                        }
                      }}
                    >
                      {mrForLoading ? (
                        <MenuItem value="">Loading...</MenuItem>
                      ) : mrForOptions.length === 0 ? (
                        <MenuItem value="">No data available</MenuItem>
                      ) : (
                        [
                          <MenuItem key="empty" value="">-- Select {header.mr_type} --</MenuItem>,
                          ...mrForOptions.map((opt: any) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          ))
                        ]
                      )}
                    </TextField>
                  </div>
                )}

                {/* Row 3 */}
                <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Vendor"
                    disabled={!allowEdit}
                    value={header.vendor_name || ''}
                    onChange={e => setHeader(h => ({ ...h, vendor_name: e.target.value }))}
                  />
                </div>
                <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date of Issue"
                    type="date"
                    disabled={!allowEdit}
                    value={header.date_of_issue || ''}
                    onChange={e => setHeader(h => ({ ...h, date_of_issue: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>

                {/* Row 4 */}
                <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Currency"
                    disabled={!allowEdit}
                    value={header.currency || ''}
                    onChange={e => setHeader(h => ({ ...h, currency: e.target.value }))}
                    placeholder="SGD"
                  />
                </div>
                <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Created By"
                    disabled={!allowEdit}
                    value={header.created_by || ''}
                    onChange={e => setHeader(h => ({ ...h, created_by: e.target.value }))}
                  />
                </div>

                {/* PO Reference (shown when PO is raised for this MR) */}
                {header.po_for && (
                  <>
                    <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="PO Reference"
                        value={header.po_for}
                        disabled
                      />
                    </div>
                    <div style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }} />
                  </>
                )}

                {/* Row 5 - GST (conditional) */}
                {header.is_chargeable && (
                  <>
                    <div className="mr-no-print" style={{ borderBottom: '1px solid #e5e7eb', padding: '10px 0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="GST %"
                        type="number"
                        disabled={!allowEdit}
                        value={header.gst || ''}
                        onChange={e => setHeader(h => ({ ...h, gst: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </div>
                    <div></div>
                  </>
                )}
              </div>
              {!isViewMode && <div className="mr-no-print" style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{serialHint()}</div>}

              {/* Items table header with actions */}
              <Box className="mr-no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                {!isViewMode && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!allowEdit}
                      onClick={() => setItems([{ _id: `temp_${Date.now()}`, item_no: 1, item_description: '', item_quantity: 1, item_measurement: 'lot', buying_price: 0, charging_price: 0, delivery_charge: 0, category_id: null, bli_id: null, rsi_id: null }])}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!allowEdit}
                      startIcon={<span>+</span>}
                      onClick={addItem}
                    >
                      Add Item
                    </Button>
                  </>
                )}
              </Box>

              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={mrItemRows}
                  columns={mrItemColumns}
                  getRowId={(row) => row._id}
                  editMode="row"
                  rowModesModel={mrItemRowModesModel}
                  onRowModesModelChange={handleMrItemRowModesModelChange}
                  onRowClick={(params) => setSelectedMrItemRowId(params.id)}
                  onRowDoubleClick={(params) => {
                    if (!isViewMode && params.row) {
                      setMrItemRowModesModel(prev => ({ ...prev, [params.id]: { mode: GridRowModes.Edit } }));
                    }
                  }}
                  onRowEditStop={handleMrItemRowEditStop}
                  processRowUpdate={processMrItemRowUpdate}
                  autoHeight
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } }
                  }}
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 300 },
                      printOptions: { disableToolbarButton: true },
                      csvOptions: { disableToolbarButton: true },
                    },
                  }}
                  rowHeight={48}
                  rowSelectionModel={
                    selectedMrItemRowId != null
                      ? { type: 'include', ids: new Set([selectedMrItemRowId]) }
                      : { type: 'include', ids: new Set() }
                  }
                  sx={{
                    ...dataGridSx,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    '& .MuiDataGrid-row': {
                      ...dataGridSx['& .MuiDataGrid-row'],
                      cursor: isViewMode ? 'default' : 'pointer'
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '8px 12px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      gap: 1,
                    }
                  }}
                />
              </Box>

              {/* Note field */}
              <Box className="mr-no-print" sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, pt: 1, minWidth: 40 }}>Note</Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  disabled={!allowEdit}
                  value={header.note || ''}
                  onChange={e => setHeader(h => ({ ...h, note: e.target.value }))}
                  placeholder="Optional note under items"
                  variant="standard"
                />
              </Box>

              {/* Totals */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                {chg ? (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">GST {totals.gstPct.toFixed(2)}%: {formatCurrency(totals.gstAmt, header.currency || 'SGD', companyData.number_format)}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>GROSS: {formatCurrency(totals.gross, header.currency || 'SGD', companyData.number_format)}</Typography>
                  </Box>
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Grand Total: {formatCurrency(totals.gross, header.currency || 'SGD', companyData.number_format)}
                  </Typography>
                )}
              </Box>

              {/* Bottom table: reliable page break handling */}
              <table className="mr-no-print" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
                <tbody>
                  {/* Row 1 */}
                  <tr>
                    <td style={{ width: '50%', paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Delivery Venue" disabled={!allowEdit} value={header.delivery_venue || ''} onChange={e => setHeader(h => ({ ...h, delivery_venue: e.target.value }))} />
                    </td>
                    <td style={{ width: '50%', paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Delivery Date" type="date" disabled={!allowEdit} value={header.delivery_date || ''} onChange={e => setHeader(h => ({ ...h, delivery_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
                    </td>
                  </tr>
                  {/* Row 2 */}
                  <tr>
                    <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Recipient" disabled={!allowEdit} value={header.delivery_recipient || ''} onChange={e => setHeader(h => ({ ...h, delivery_recipient: e.target.value }))} />
                    </td>
                    <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="HP / Contact" disabled={!allowEdit} value={header.delivery_hp || ''} onChange={e => setHeader(h => ({ ...h, delivery_hp: e.target.value }))} />
                    </td>
                  </tr>
                  {/* Row 3 */}
                  <tr>
                    <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Ordered By" disabled={!allowEdit} value={header.ordered_by || ''} onChange={e => setHeader(h => ({ ...h, ordered_by: e.target.value }))} />
                    </td>
                    <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Approved By" disabled={true} value={header.approved_by || ''} onChange={e => setHeader(h => ({ ...h, approved_by: e.target.value }))} helperText="Auto-filled when MR is approved from Budget Dashboard" />
                    </td>
                  </tr>
                  {/* Non-chargeable specific fields */}
                  {!chg && (
                    <>
                      {/* Row 4 - Contract Period */}
                      <tr>
                        <td colSpan={2} style={{ paddingBottom: 12, verticalAlign: 'top' }}>
                          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>Contract Period</Typography>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <TextField size="small" label="From" type="date" disabled={!allowEdit} value={header.contract_from || ''} onChange={e => setHeader(h => ({ ...h, contract_from: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                            <TextField size="small" label="To" type="date" disabled={!allowEdit} value={header.contract_to || ''} onChange={e => setHeader(h => ({ ...h, contract_to: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
                          </div>
                        </td>
                      </tr>
                      {/* Row 5 */}
                      <tr>
                        <td style={{ paddingBottom: 12, paddingRight: 8, verticalAlign: 'top' }}>
                          <TextField fullWidth size="small" label="DLP / Warranty (months)" type="number" disabled={!allowEdit} value={header.warranty_months || header.defects_liability_months || ''} onChange={e => setHeader(h => ({ ...h, warranty_months: e.target.value, defects_liability_months: e.target.value }))} inputProps={{ min: 0 }} />
                        </td>
                        <td style={{ paddingBottom: 12, paddingLeft: 8, verticalAlign: 'top' }}>
                          <TextField select fullWidth size="small" label="Maintenance Services" disabled={!allowEdit} value={header.maintenance_service || 'NA'} onChange={e => setHeader(h => ({ ...h, maintenance_service: e.target.value }))}>
                            <MenuItem value="Monthly">Monthly</MenuItem>
                            <MenuItem value="Bi-Monthly">Bi-Monthly</MenuItem>
                            <MenuItem value="Quarterly">Quarterly</MenuItem>
                            <MenuItem value="Yearly">Yearly</MenuItem>
                            <MenuItem value="NA">NA</MenuItem>
                          </TextField>
                        </td>
                      </tr>
                    </>
                  )}
                  {/* Remarks */}
                  <tr>
                    <td colSpan={2} style={{ verticalAlign: 'top' }}>
                      <TextField fullWidth size="small" label="Remarks" multiline rows={2} disabled={!allowEdit} value={header.remarks || ''} onChange={e => setHeader(h => ({ ...h, remarks: e.target.value }))} />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Bottom-right: Budget selector (Design Changes and Contracts) - HIDDEN FOR NOW */}
              <div style={{ display: 'none', gridTemplateColumns: '120px minmax(0, 1fr)', alignItems: 'center', columnGap: 8 }}>
                <b>Budget:</b>
                <div>
                    <div className="mr-no-print">
                      <Autocomplete
                        size="small"
                        options={budgetOptions}
                        value={header.budget_selection || null}
                        onChange={(_, v) => {
                          const newHeader = { ...header, budget_selection: v };
                          if (v?.type === 'design_change') {
                            newHeader.design_change = v.value;
                            newHeader.contract_id = null;
                          } else if (v?.type === 'contract') {
                            newHeader.design_change = '';
                            newHeader.contract_id = v.value; // This is now the contract ID
                          } else {
                            newHeader.design_change = '';
                            newHeader.contract_id = null;
                          }
                          setHeader(newHeader);
                        }}
                        getOptionLabel={(option) => option?.label || ''}
                        isOptionEqualToValue={(option, value) => option?.value === value?.value && option?.type === value?.type}
                        fullWidth
                        disablePortal
                        renderInput={(params) => <TextField {...params} label="Budget" fullWidth />}
                      />
                    </div>
                  {/* Print-only plain text mirror for the selected value */}
                  <div className="mr-only-print" style={{ paddingTop: 6, color: '#111' }}>
                    {header.budget_selection?.label || header.design_change || ''}
                  </div>
                </div>
              </div>
            </div>
          </Section>

        {/* Wizard Nav removed in single-screen editor */}
          </div>
        </DialogContent>
        <DialogActions>
          {isViewMode ? (
            <>
              <Button onClick={handleCloseDialog}>Close</Button>
              {canEdit && header.status !== 'Approved' && (
                <Button
                  onClick={() => {
                    setEditingApprovedMR(header.status !== 'Draft');
                    setIsViewMode(false);
                  }}
                  variant="outlined"
                  color="primary"
                >
                  Edit
                </Button>
              )}
              <Button onClick={() => {
                const prev = document.title;
                document.title = `MR-${header.mr_serial_no || 'draft'}`;
                window.print();
                document.title = prev;
              }} variant="contained" color="primary">
                Print / PDF
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={async () => { await saveAll(); handleCloseDialog(); fetchExisting(); }} variant="contained" color="primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              {selectedSerial && !editingApprovedMR && (header.status === 'Draft' || header.status === 'Rejected') && (
                <Button
                  onClick={async () => {
                    const serial = selectedSerial || header.mr_serial_no;
                    await saveAll();
                    if (serial) await handleSendForApproval(serial);
                    handleCloseDialog();
                    fetchExisting();
                  }}
                  variant="contained"
                  color="success"
                  disabled={saving}
                >
                  Send for Approval
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog
        open={poDialogOpen}
        onClose={handleClosePODialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Purchase Order Details</span>
            {selectedPO && <span style={{ fontSize: 14, color: '#666' }}>PO: {selectedPO.po_number}</span>}
          </div>
        </DialogTitle>
        <DialogContent>
          {loadingPO ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
              <LinearProgress sx={{ width: '50%' }} />
            </div>
          ) : selectedPO ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* PO Header Information */}
              <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: 16, fontWeight: 600 }}>General Information</Typography>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <Typography variant="caption" color="text.secondary">PO Number</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedPO.po_number}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2">
                      <span style={{
                        background: selectedPO.status === 'Approved' ? '#e8f5e9' : '#f5f5f5',
                        color: selectedPO.status === 'Approved' ? '#2e7d32' : '#666',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600
                      }}>
                        {selectedPO.status || 'Draft'}
                      </span>
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Vendor</Typography>
                    <Typography variant="body2">{selectedPO.vendor_name || '-'}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Currency</Typography>
                    <Typography variant="body2">{selectedPO.po_currency || 'SGD'}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Date Issued</Typography>
                    <Typography variant="body2">{selectedPO.date_issued || '-'}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Delivery Date</Typography>
                    <Typography variant="body2">{selectedPO.delivery_date || '-'}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Created By</Typography>
                    <Typography variant="body2">{selectedPO.created_by || '-'}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">MR Reference</Typography>
                    <Typography variant="body2" fontWeight={600} color="primary">{selectedPO.mr_serial_no || '-'}</Typography>
                  </div>
                </div>
              </div>

              {/* PO Items */}
              {selectedPO.items && selectedPO.items.length > 0 && (
                <div>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: 16, fontWeight: 600 }}>Items ({selectedPO.items.length})</Typography>
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    <DataGrid
                      rows={selectedPO.items.map((item: any, idx: number) => ({ ...item, _viewId: item.item_no || idx + 1 }))}
                      columns={[
                        { field: 'item_no', headerName: '#', width: 70 },
                        { field: 'item_description', headerName: 'Description', flex: 1, minWidth: 150 },
                        { field: 'item_quantity', headerName: 'Qty', width: 100, type: 'number',
                          renderCell: (params: any) => `${params.value || 0} ${params.row.item_measurement || ''}` },
                        { field: 'buying_price', headerName: 'Unit Price', width: 130, type: 'number',
                          valueFormatter: (value: any) => formatCurrency(value || 0, selectedPO.po_currency || 'SGD', companyData.number_format) },
                        { field: 'line_total', headerName: 'Total', width: 140, type: 'number',
                          valueGetter: (value: any, row: any) => (row.item_quantity || 0) * (row.buying_price || 0),
                          valueFormatter: (value: any) => formatCurrency(value || 0, selectedPO.po_currency || 'SGD', companyData.number_format) },
                      ] as GridColDef[]}
                      getRowId={(row) => row._viewId}
                      getRowHeight={() => 'auto'}
                      pageSizeOptions={[10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      disableRowSelectionOnClick
                      density="compact"
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 300 },
                          printOptions: { disableToolbarButton: true },
                          csvOptions: { disableToolbarButton: true },
                        },
                      }}
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-toolbarContainer': { padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', gap: 1 },
                        '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                        '& .MuiDataGrid-row': { '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 }, '&:hover': { backgroundColor: 'action.hover' } },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary">
                      Grand Total: {formatCurrency(selectedPO.items.reduce((sum: number, item: any) => sum + ((item.item_quantity || 0) * (item.buying_price || 0)), 0), selectedPO.po_currency || 'SGD', companyData.number_format)}
                    </Typography>
                  </Box>
                </div>
              )}

              {/* Notes */}
              {selectedPO.notes && (
                <div>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: 16, fontWeight: 600 }}>Notes</Typography>
                  <Typography variant="body2" sx={{ background: '#f9f9f9', padding: 2, borderRadius: 1 }}>
                    {selectedPO.notes}
                  </Typography>
                </div>
              )}
            </div>
          ) : (
            <Typography variant="body2" color="text.secondary">No PO data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePODialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Material Requisition</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this Material Requisition?
          </Typography>
          {mrToDelete && (
            <Typography variant="body2" sx={{ background: '#ffebee', color: '#c62828', padding: 2, borderRadius: 1, fontWeight: 600 }}>
              MR Number: {mrToDelete}
            </Typography>
          )}
          {mrToDelete && existing.find(mr => mr.mr_serial_no === mrToDelete)?.status === 'Approved' && (
            <Typography variant="body2" sx={{ background: '#fff3e0', color: '#e65100', padding: 2, borderRadius: 1, fontWeight: 600, mt: 2 }}>
              ⚠️ Warning: This MR is approved and has an associated Purchase Order. Both the MR and PO will be permanently deleted.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. All items and associated data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View MR Details Dialog */}
      <Dialog open={viewMRDialog.open} onClose={handleViewMRClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Material Requisition Details</Typography>
            {viewMRDialog.data && (
              <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>{viewMRDialog.data.mr_serial_no}</Typography>
            )}
          </Box>
          {viewMRDialog.data && (
            <Chip
              label={viewMRDialog.data.status || 'Draft'}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: 'white',
                color: viewMRDialog.data.status === 'Approved' ? '#2e7d32' : viewMRDialog.data.status === 'Rejected' ? '#c62828' : '#1976d2'
              }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {viewMRDialog.data && (
            <Box>
              {/* Manager Rejection Comments */}
              {viewMRDialog.data.rejection_comments && (
                <Box sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: '#fff5f5',
                  border: '2px solid #f44336',
                  borderLeft: '6px solid #f44336',
                  borderRadius: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start'
                }}>
                  <Box sx={{ color: '#f44336', mt: 0.2, fontSize: 20, fontWeight: 'bold', lineHeight: 1 }}>⚠</Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#c62828', mb: 0.5 }}>
                      Manager Rejection Comments
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#7f1d1d', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontWeight: 500 }}>
                      {viewMRDialog.data.rejection_comments}
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* Basic Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Basic Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">MR Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewMRDialog.data.mr_type || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Vendor</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewMRDialog.data.vendor_name || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Is Chargeable</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewMRDialog.data.is_chargeable ? 'Yes' : 'No'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Contract / VO / DC</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {viewMRDialog.data.contract_id || viewMRDialog.data.vo_id || viewMRDialog.data.change_number || 'N/A'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Currency</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewMRDialog.data.currency || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Date of Issue</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewMRDialog.data.date_of_issue || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Line Items */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Line Items</Typography>
              {viewMRDialog.loadingItems ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={36} />
                </Box>
              ) : viewMRDialog.items.length > 0 ? (
                <Box sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                  <DataGrid
                    rows={viewMRDialog.items.map((item: any, idx: number) => ({
                      ...item,
                      _viewId: item.item_no || idx + 1,
                    }))}
                    columns={(() => {
                      const toNum = (v: any) => (v === '' || v === null || v === undefined) ? 0 : +v;
                      const cur = viewMRDialog.data?.currency || 'SGD';
                      return [
                        { field: 'item_no', headerName: '#', width: 70, valueGetter: (value: any, row: any) => value || viewMRDialog.items.indexOf(row) + 1 },
                        { field: 'item_description', headerName: 'Description', flex: 1, minWidth: 150 },
                        { field: 'item_quantity', headerName: 'Qty', width: 100, type: 'number', valueFormatter: (value: any) => value != null ? toNum(value).toFixed(2) : '0.00' },
                        { field: 'buying_price', headerName: 'Buying Price', width: 130, type: 'number', valueFormatter: (value: any) => formatCurrency(toNum(value), cur, companyData.number_format) },
                        { field: 'charging_price', headerName: 'Charging Price', width: 130, type: 'number', valueFormatter: (value: any) => formatCurrency(toNum(value), cur, companyData.number_format) },
                        { field: 'delivery_charge', headerName: 'Delivery', width: 120, type: 'number', valueFormatter: (value: any) => formatCurrency(toNum(value), cur, companyData.number_format) },
                        { field: 'pm_comment', headerName: 'PM Comment', flex: 1, minWidth: 150, renderCell: (params: any) => (
                          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5, py: 0.5, fontSize: '0.8rem', color: params.value ? 'text.primary' : 'text.secondary', fontStyle: params.value ? 'normal' : 'italic' }}>
                            {params.value || '-'}
                          </Typography>
                        )},
                      ] as GridColDef[];
                    })()}
                    getRowId={(row) => row._viewId || Math.random()}
                    getRowHeight={() => 'auto'}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        printOptions: { disableToolbarButton: true },
                        csvOptions: { disableToolbarButton: true },
                      },
                    }}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-toolbarContainer': { padding: '8px 12px', borderBottom: '1px solid', borderColor: 'divider', gap: 1 },
                      '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 1 },
                      '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.100', borderBottom: '2px solid', borderColor: 'divider', fontSize: '0.8rem', fontWeight: 700 },
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.8rem' },
                      '& .MuiDataGrid-row': { '&:nth-of-type(even)': { backgroundColor: 'action.hover', opacity: 0.8 }, '&:hover': { backgroundColor: 'action.hover' } },
                    }}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No items found</Typography>
              )}

              {/* Totals */}
              {viewMRDialog.items.length > 0 && (() => {
                const toNum = (v: any) => (v === '' || v === null || v === undefined) ? 0 : +v;
                const cur = viewMRDialog.data?.currency || 'SGD';
                const cost = viewMRDialog.items.reduce((s: number, it: any) => s + toNum(it.item_quantity) * toNum(it.buying_price), 0);
                const bill = viewMRDialog.items.reduce((s: number, it: any) => s + toNum(it.item_quantity) * toNum(it.charging_price), 0);
                const delivery = viewMRDialog.items.reduce((s: number, it: any) => s + toNum(it.delivery_charge), 0);
                const grandTotal = viewMRDialog.data.is_chargeable ? (bill + delivery) : (cost + delivery);
                return (
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Totals</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Cost (Buying)</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(cost + delivery, cur, companyData.number_format)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Billing (Charging)</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(bill + delivery, cur, companyData.number_format)}</Typography>
                      </Box>
                      <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                        <Typography variant="body1" fontWeight={700} color="primary">{formatCurrency(grandTotal, cur, companyData.number_format)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewMRClose} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {importSourceOpen && importSourceDialog}
      {mappingOpen && mappingDialog}
      {importing && importingDialog}

      {/* Import Summary Dialog */}
      <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Summary</DialogTitle>
        <DialogContent>
          {importSummaryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>Total MRs processed: <strong>{importSummaryData.total}</strong></Typography>
              <Typography variant="body2" sx={{ color: 'green' }}>Added: {importSummaryData.added}</Typography>
              <Typography variant="body2" sx={{ color: 'goldenrod' }}>Overwritten: {importSummaryData.overwritten}</Typography>
              <Typography variant="body2" sx={{ color: 'grey' }}>Skipped: {importSummaryData.skipped}</Typography>
              <Typography variant="body2" sx={{ color: 'red', mb: 2 }}>Failed: {importSummaryData.failed}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total changes: {importSummaryData.added + importSummaryData.overwritten}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => downloadImportLog(importSummaryData)} variant="outlined">Download Import Log</Button>
          <Button onClick={() => setImportSummaryOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaterialRequisitions;



























